<template>
  <div>
    <v-row no-gutters>
      <v-card-title class="headline">
        Einführung Energiecontrolling – VALERI Untersuchung
      </v-card-title>
      <v-row no-gutters class="justify-end">
        <div>
          <!--
            `flex-wrap` und `ga-2`: die Leiste traegt jetzt eine Schaltflaeche
            mehr („Hilfe"). Ohne Umbruch quetscht sie bei geoeffneter
            Hilfe-Schublade die Beschriftung des Schalters auf einen Buchstaben
            je Zeile. `flex-shrink-0` haelt den Schalter auf seiner Breite,
            statt ihn als erstes einzudruecken.
          -->
          <v-container class="d-flex align-center flex-wrap justify-end ga-2">
            <v-switch
              v-model="showGroupDetails"
              dense
              class="flex-shrink-0"
              hide-details
              inset
              label="Gruppendetails anzeigen"
            />
            <ProjectPageNav />
            <v-btn
              color="primary"
              :loading="loading"
              @click="saveDefaults"
              title="VALERI-Parameter im Projekt speichern"
            >
              <v-icon start>mdi-content-save</v-icon>
              Werte Speichern
            </v-btn>
            <v-btn
              color="success"
              :loading="isExporting"
              :disabled="isExporting || !scenarioResponses.probable"
              @click="exportValeriExcel"
              title="VALERI als Excel exportieren"
            >
              <v-icon start>mdi-file-excel</v-icon>
              Excel Export
            </v-btn>
            <!--
              `HelpBar` ist nur die Schublade; geoeffnet wird sie ueber
              `store.showSidebar(<Hilfekomponente>)`. Das fruehere
              `<HelpBar :help="ValeriHelp" />` reichte eine Eigenschaft herein, die
              es an `HelpBar` gar nicht gibt — die Hilfe liess sich auf dieser Seite
              also nie oeffnen. So macht es die Seite Einsparpotential schon immer.
            -->
            <v-tooltip text="Hilfe anzeigen" location="bottom" :disabled="store.sidebarShown">
              <template #activator="{ props }">
                <v-btn
                  v-bind="props"
                  color="primary"
                  variant="flat"
                  class="mr-4"
                  @click="store.sidebarShown ? store.hideSidebar() : store.showSidebar(ValeriHelp)"
                >
                  <v-icon start>
                    {{ store.sidebarShown ? 'mdi-close' : 'mdi-help-circle-outline' }}
                  </v-icon>
                  Hilfe
                </v-btn>
              </template>
            </v-tooltip>
            <HelpBar />
          </v-container>
        </div>
      </v-row>
    </v-row>

    <div v-if="errorResponse">
      <v-card-text>
        <v-alert type="error" variant="outlined">
          <div>{{ errorResponse }}</div>
        </v-alert>
      </v-card-text>
    </div>

    <div v-if="currentErrors.length">
      <v-card-text>
        <v-alert type="error" variant="outlined">
          <div class="font-weight-bold mb-1">
            Einzelne Maßnahmen konnten nicht gerechnet werden. Ihre Zeilen in der
            Tabelle bleiben leer.
          </div>
          <div v-for="fehler in currentErrors" :key="fehler">{{ fehler }}</div>
        </v-alert>
      </v-card-text>
    </div>

    <div v-if="currentWarnings.length">
      <v-card-text>
        <v-alert type="warning" variant="outlined">
          <div v-for="warning in currentWarnings" :key="warning">{{ warning }}</div>
        </v-alert>
      </v-card-text>
    </div>

    <!--
      Gerechnet wird nur beim Aufbau und auf Knopfdruck. Ohne diesen Hinweis
      sah man geänderte Eingaben neben unveränderten Ergebnissen und konnte
      nicht erkennen, welche der beiden Zahlen gilt.
    -->
    <div v-if="settingsDirty">
      <v-card-text>
        <v-alert type="info" variant="outlined">
          <div class="d-flex align-center flex-wrap">
            <span class="mr-4">
              Die Eingabeparameter wurden geändert. Tabellen und Grafiken zeigen
              weiterhin die letzte Berechnung.
            </span>
            <v-btn
              color="primary"
              size="small"
              :loading="loading"
              @click="calcValeri"
            >
              <v-icon start>mdi-refresh</v-icon>
              Neu berechnen
            </v-btn>
          </div>
        </v-alert>
      </v-card-text>
    </div>

    <div class="pa-4">
      <v-card class="chart-section pa-4" variant="outlined">
        <div class="heading mb-4">Eingabeparameter</div>

        <!--
          Herleitung des Kalkulationszinssatzes nach Anhang 5.1 der VALERI-Vorlage.
          Der Eigenkapitalanteil gilt fuer das ganze Projekt, die beiden Zinssaetze
          schwanken je Szenario und stehen deshalb unten in der Tabelle.
          Bleibt das Feld leer, gilt weiterhin der gesetzte Kalkulationszinssatz.
        -->
        <v-row class="mb-2 wacc-block" no-gutters>
          <v-col cols="12" md="4" class="pr-md-4">
            <v-text-field
              v-model.number="equitySharePercent"
              label="Eigenkapitalanteil"
              suffix="%"
              type="number"
              step="1"
              min="0"
              max="100"
              density="compact"
              variant="outlined"
              hide-details="auto"
              clearable
              :error-messages="equityShareError"
            />
          </v-col>
          <v-col cols="12" md="8" class="d-flex align-center">
            <span class="text-caption text-medium-emphasis">{{ waccHinweis }}</span>
          </v-col>
        </v-row>

        <v-table density="comfortable">
          <thead>
            <tr>
              <th>Einstellparameter</th>
              <th class="text-right">Wahrscheinlich</th>
              <th class="text-right">Worst-Case</th>
              <th class="text-right">Best-Case</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="parameter in parameterRows" :key="parameter.key">
              <td>{{ parameter.label }}</td>
              <td class="scenario-input-cell">
                <v-text-field
                  v-model.number="scenarioSettings.probable[parameter.key]"
                  dense
                  variant="outlined"
                  hide-details
                  :type="parameter.type || 'number'"
                  :step="parameter.step || '1'"
                  :suffix="parameter.suffix"
                />
              </td>
              <td class="scenario-input-cell">
                <v-text-field
                  v-model.number="scenarioSettings.worstCase[parameter.key]"
                  dense
                  variant="outlined"
                  hide-details
                  :type="parameter.type || 'number'"
                  :step="parameter.step || '1'"
                  :suffix="parameter.suffix"
                />
              </td>
              <td class="scenario-input-cell">
                <v-text-field
                  v-model.number="scenarioSettings.bestCase[parameter.key]"
                  dense
                  variant="outlined"
                  hide-details
                  :type="parameter.type || 'number'"
                  :step="parameter.step || '1'"
                  :suffix="parameter.suffix"
                />
              </td>
            </tr>
            <tr class="total-row" v-if="scenarioResponses.probable || scenarioResponses.worstCase || scenarioResponses.bestCase">
              <td>Kapitalwert (NPV)</td>
              <td class="text-right">{{ formatEuro(scenarioResponses.probable && scenarioResponses.probable.result && scenarioResponses.probable.result.total ? scenarioResponses.probable.result.total.capitalValue : null) }}</td>
              <td class="text-right">{{ formatEuro(scenarioResponses.worstCase && scenarioResponses.worstCase.result && scenarioResponses.worstCase.result.total ? scenarioResponses.worstCase.result.total.capitalValue : null) }}</td>
              <td class="text-right">{{ formatEuro(scenarioResponses.bestCase && scenarioResponses.bestCase.result && scenarioResponses.bestCase.result.total ? scenarioResponses.bestCase.result.total.capitalValue : null) }}</td>
            </tr>
          </tbody>
        </v-table>
      </v-card>

      <v-card class="chart-section pa-4 mt-4" variant="outlined">
        <div class="heading mb-4">Maßnahmen</div>
        <v-row class="mb-4" no-gutters>
          <v-col cols="12" md="8">
            <v-btn-toggle v-model="activeScenario" mandatory>
              <v-btn value="probable">Wahrscheinlich</v-btn>
              <v-btn value="worstCase">Worst-Case</v-btn>
              <v-btn value="bestCase">Best-Case</v-btn>
            </v-btn-toggle>
          </v-col>
          <v-col cols="12" md="4" class="d-flex justify-end align-center">
            <span class="font-weight-bold">Aktives Szenario: {{ scenarioLabel(activeScenario) }}</span>
          </v-col>
        </v-row>

        <div v-if="currentTotalResult">
          <v-table density="comfortable">
            <thead>
              <tr>
                <th>Name</th>
                <th>Maßnahme</th>
                <th class="text-right">Nutzungsdauer [a]</th>
                <th class="text-right">Investition [€]</th>
                <th class="text-right">Netto-Cashflow Jahr 1 (inkl. Degradation) [€/a]</th>
                <th class="text-right">Restwert [€]</th>
                <th class="text-right">Kapitalwert [€]</th>
                <th class="text-right">Annuität [€/a]</th>
                <th class="text-right">Dynamische Amortisation [a]</th>
              </tr>
            </thead>
            <tbody
              v-for="group in currentActionGroupResults"
              :key="`group-wrap-${activeScenario}-${group.id}`"
            >
              <tr class="group-row" @click="toggleScenarioGroup(activeScenario, group.id)">
                <td>{{ group.name }}</td>
                <td>Gruppe</td>
                <td class="text-right">-</td>
                <td class="text-right">{{ formatNumber(group.invest) }}</td>
                <td class="text-right">{{ formatNumber(group.annualNetBenefit) }}</td>
                <td class="text-right">{{ formatNumber(group.residualValue) }}</td>
                <td class="text-right">{{ formatNumber(group.capitalValue) }}</td>
                <td class="text-right">{{ formatNumber(group.annuity) }}</td>
                <td class="text-right">{{ formatPayback(group.discountedPayback) }}</td>
              </tr>
              <tr
                v-for="action in group.actions"
                :key="`group-action-${activeScenario}-${action.id}`"
                class="action-row"
                v-show="isScenarioGroupExpanded(activeScenario, group.id)"
              >
                <td class="pl-8">{{ action.name }}</td>
                <td>{{ getMethodName(action.type) }}</td>
                <td class="text-right useful-life-cell">
                  <v-text-field
                    v-model.number="usefulLifeYears[action.id]"
                    dense
                    hide-details
                    variant="outlined"
                    type="number"
                    min="1"
                    class="useful-life-input"
                  />
                </td>
                <td class="text-right">{{ formatNumber(action.invest) }}</td>
                <td class="text-right">{{ formatNumber(action.annualNetBenefit) }}</td>
                <td class="text-right">{{ formatNumber(action.residualValue) }}</td>
                <td class="text-right">{{ formatNumber(action.capitalValue) }}</td>
                <td class="text-right">{{ formatNumber(action.annuity) }}</td>
                <td class="text-right">{{ formatPayback(action.discountedPayback) }}</td>
              </tr>
            </tbody>

            <tbody>
              <tr
                v-for="action in currentActionResults"
                :key="`action-${activeScenario}-${action.id}`"
                class="action-row"
              >
                <td>{{ action.name }}</td>
                <td>{{ getMethodName(action.type) }}</td>
                <td class="text-right useful-life-cell">
                  <v-text-field
                    v-model.number="usefulLifeYears[action.id]"
                    dense
                    hide-details
                    variant="outlined"
                    type="number"
                    min="1"
                    class="useful-life-input"
                  />
                </td>
                <td class="text-right">{{ formatNumber(action.invest) }}</td>
                <td class="text-right">{{ formatNumber(action.annualNetBenefit) }}</td>
                <td class="text-right">{{ formatNumber(action.residualValue) }}</td>
                <td class="text-right">{{ formatNumber(action.capitalValue) }}</td>
                <td class="text-right">{{ formatNumber(action.annuity) }}</td>
                <td class="text-right">{{ formatPayback(action.discountedPayback) }}</td>
              </tr>

              <tr class="total-row">
                <td>Summe</td>
                <td></td>
                <td class="text-right">-</td>
                <td class="text-right">{{ formatNumber(currentTotalResult.invest) }}</td>
                <td class="text-right">{{ formatNumber(currentTotalResult.annualNetBenefit) }}</td>
                <td class="text-right">{{ formatNumber(currentTotalResult.residualValue) }}</td>
                <td class="text-right">{{ formatNumber(currentTotalResult.capitalValue) }}</td>
                <td class="text-right">{{ formatNumber(currentTotalResult.annuity) }}</td>
                <td class="text-right">{{ formatPayback(currentTotalResult.discountedPayback) }}</td>
              </tr>
            </tbody>
          </v-table>
        </div>
      </v-card>

      <div class="mt-4">
        <div class="heading mb-4"></div>
        <div v-if="hasAnyVisualizationData">
          <v-row no-gutters>
            <v-col
              v-if="hasChartData"
              cols="12"
              :md="hasNpvTornadoData ? 6 : 12"
              class="pr-md-2"
            >
              <v-card class="chart-section pa-4" variant="outlined">
            <div class="chart-title d-flex align-center">
              <span class="chart-title-text">Szenarien der Kapitalwertentwicklung</span>
              <v-spacer />
              <v-tooltip location="bottom">
                <template v-slot:activator="{ props }">
                  <v-btn
                    icon
                    x-small
                    color="secondary"
                    class="ml-2"
                    v-bind="props"
                  >
                    <v-icon small>mdi-information-outline</v-icon>
                  </v-btn>
                </template>
                <span>Der letzte Punkt enthält den abgezinsten Restwert (falls vorhanden).</span>
              </v-tooltip>
              <v-menu>
                <template v-slot:activator="{ props }">
                  <v-btn
                    icon
                    color="primary"
                    v-bind="props"
                    title="Optionen"
                  >
                    <v-icon>mdi-dots-vertical</v-icon>
                  </v-btn>
                </template>
                <v-list density="compact">
                  <v-list-item @click="downloadChartImageByRef('valeriScenarioChart', 'valeri-szenario')">
                    <template v-slot:prepend><v-icon>mdi-image</v-icon></template>
                    <v-list-item-title>Grafik herunterladen</v-list-item-title>
                  </v-list-item>
                  <v-list-item @click="exportChartDataExcelGeneric(scenarioChartData, 'valeri-szenario')">
                    <template v-slot:prepend><v-icon>mdi-file-excel</v-icon></template>
                    <v-list-item-title>Daten exportieren</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </div>
            <div class="chart-wrapper">
              <!--
                `height` auf CHART_HEIGHT, damit diese Karte mit der
                Nachbarkarte derselben Reihe abschliesst. Die Balkendiagramme
                tragen keinen eigenen Wrapper und folgen direkt der Höhe von
                `.chart-wrapper`; LineChart bringt einen mit und braucht die
                Angabe deshalb ausdrücklich.
              -->
              <line-chart
                ref="valeriScenarioChart"
                :chart-data="scenarioChartData"
                :options="chartOptions"
                :height="CHART_HEIGHT"
              />
            </div>
              </v-card>
            </v-col>

            <v-col
              v-if="hasNpvTornadoData"
              cols="12"
              :md="hasChartData ? 6 : 12"
              class="pl-md-2"
              :class="{ 'mt-4 mt-md-0': hasChartData }"
            >
              <v-card class="chart-section pa-4" variant="outlined">
            <div class="chart-title d-flex align-center">
              <span class="chart-title-text">NPV Tornado je Maßnahme</span>
              <v-spacer />
              <v-tooltip location="bottom" max-width="420">
                <template v-slot:activator="{ props }">
                  <v-btn
                    icon
                    x-small
                    color="secondary"
                    class="ml-2"
                    v-bind="props"
                  >
                    <v-icon small>mdi-information-outline</v-icon>
                  </v-btn>
                </template>
                <span>Zeigt die Bandbreite des Kapitalwerts je Maßnahme in Euro über alle Szenarien. Längere Balken bedeuten höhere Sensitivität und Unsicherheit, die Markierung zeigt den wahrscheinlichen Wert.</span>
              </v-tooltip>
              <v-menu>
                <template v-slot:activator="{ props }">
                  <v-btn
                    icon
                    color="primary"
                    v-bind="props"
                    title="Optionen"
                  >
                    <v-icon>mdi-dots-vertical</v-icon>
                  </v-btn>
                </template>
                <v-list density="compact">
                  <v-list-item @click="downloadChartImageByRef('valeriTornadoChart', 'valeri-tornado')">
                    <template v-slot:prepend><v-icon>mdi-image</v-icon></template>
                    <v-list-item-title>Grafik herunterladen</v-list-item-title>
                  </v-list-item>
                  <v-list-item @click="exportChartDataExcelGeneric(npvTornadoData, 'valeri-tornado')">
                    <template v-slot:prepend><v-icon>mdi-file-excel</v-icon></template>
                    <v-list-item-title>Daten exportieren</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </div>
            <div class="chart-wrapper">
              <horizontal-bar-chart
                ref="valeriTornadoChart"
                :chart-data="npvTornadoData"
                :options="npvTornadoOptions"
              />
            </div>
              </v-card>
            </v-col>
          </v-row>

          <v-row no-gutters class="mt-4">
            <v-col
              v-if="hasActiveCashflowStackData"
              cols="12"
              :md="hasPaybackTimelineData ? 6 : 12"
              class="pr-md-2"
            >
              <v-card class="chart-section pa-4" variant="outlined">
            <div class="chart-title d-flex align-center">
              <span class="chart-title-text">Jährlicher Cashflow je Maßnahme (aktives Szenario)</span>
              <v-spacer />
              <v-tooltip location="bottom" max-width="420">
                <template v-slot:activator="{ props }">
                  <v-btn
                    icon
                    x-small
                    color="secondary"
                    class="ml-2"
                    v-bind="props"
                  >
                    <v-icon small>mdi-information-outline</v-icon>
                  </v-btn>
                </template>
                <!--
                  Hier stand der Hinweistext der Kapitalwertkurve („Der letzte
                  Punkt enthält den abgezinsten Restwert") — übernommen, aber
                  für dieses Diagramm falsch: es zeigt den Cashflow je Jahr und
                  nicht den aufsummierten Kapitalwert.
                -->
                <span>Zeigt den Netto-Cashflow jeder Maßnahme je Jahr, als Flächen übereinandergelegt — die Oberkante ist der Cashflow des Projekts in diesem Jahr. Jahre außerhalb der Nutzungsdauer einer Maßnahme stehen auf null.</span>
              </v-tooltip>
              <v-menu>
                <template v-slot:activator="{ props }">
                  <v-btn
                    icon
                    color="primary"
                    v-bind="props"
                    title="Optionen"
                  >
                    <v-icon>mdi-dots-vertical</v-icon>
                  </v-btn>
                </template>
                <v-list density="compact">
                  <v-list-item @click="downloadChartImageByRef('valeriCashflowChart', 'valeri-cashflow')">
                    <template v-slot:prepend><v-icon>mdi-image</v-icon></template>
                    <v-list-item-title>Grafik herunterladen</v-list-item-title>
                  </v-list-item>
                  <v-list-item @click="exportChartDataExcelGeneric(activeCashflowStackData, 'valeri-cashflow')">
                    <template v-slot:prepend><v-icon>mdi-file-excel</v-icon></template>
                    <v-list-item-title>Daten exportieren</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </div>
            <div class="chart-wrapper">
              <!-- Siehe die Kapitalwertkurve oben: gleiche Höhe, damit die
                   Karten der Reihe abschliessen. -->
              <line-chart
                ref="valeriCashflowChart"
                :chart-data="activeCashflowStackData"
                :options="activeCashflowStackOptions"
                :height="CHART_HEIGHT"
              />
            </div>
              </v-card>
            </v-col>

            <v-col
              v-if="hasPaybackTimelineData"
              cols="12"
              :md="hasActiveCashflowStackData ? 6 : 12"
              class="pl-md-2"
              :class="{ 'mt-4 mt-md-0': hasActiveCashflowStackData }"
            >
              <v-card class="chart-section pa-4" variant="outlined">
            <div class="chart-title d-flex align-center">
              <span class="chart-title-text">Dynamische Amortisation je Maßnahme</span>
              <v-spacer />
              <v-tooltip location="bottom" max-width="420">
                <template v-slot:activator="{ props }">
                  <v-btn
                    icon
                    x-small
                    color="secondary"
                    class="ml-2"
                    v-bind="props"
                  >
                    <v-icon small>mdi-information-outline</v-icon>
                  </v-btn>
                </template>
                <span>Zeigt die dynamische, also abgezinste Amortisationszeit je Maßnahme in Jahren, für jedes Szenario ein Balkenpaar in der Szenariofarbe. Der ausgefüllte Abschnitt ist die erreichte Amortisation; der schraffierte Abschnitt steht für Maßnahmen, die sich innerhalb der Laufzeit nicht amortisieren, und reicht dann über den ganzen Analysehorizont.</span>
              </v-tooltip>
              <v-menu>
                <template v-slot:activator="{ props }">
                  <v-btn
                    icon
                    color="primary"
                    v-bind="props"
                    title="Optionen"
                  >
                    <v-icon>mdi-dots-vertical</v-icon>
                  </v-btn>
                </template>
                <v-list density="compact">
                  <v-list-item @click="downloadChartImageByRef('valeriPaybackChart', 'valeri-payback')">
                    <template v-slot:prepend><v-icon>mdi-image</v-icon></template>
                    <v-list-item-title>Grafik herunterladen</v-list-item-title>
                  </v-list-item>
                  <v-list-item @click="exportChartDataExcelGeneric(paybackTimelineData, 'valeri-payback')">
                    <template v-slot:prepend><v-icon>mdi-file-excel</v-icon></template>
                    <v-list-item-title>Daten exportieren</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </div>
            <div class="chart-wrapper">
              <horizontal-bar-chart
                ref="valeriPaybackChart"
                :chart-data="paybackTimelineData"
                :options="paybackTimelineOptions"
              />
            </div>
              </v-card>
            </v-col>
          </v-row>

          <v-row no-gutters class="mt-4">
            <v-col
              v-if="hasAnnuityComparisonData"
              cols="12"
              :md="hasSensitivityRadarData ? 8 : 12"
              class="pr-md-2"
            >
              <v-card class="chart-section pa-4" variant="outlined">
            <div class="chart-title d-flex align-center">
              <span class="chart-title-text">Annuitätsvergleich je Maßnahme</span>
              <v-spacer />
              <v-tooltip location="bottom" max-width="420">
                <template v-slot:activator="{ props }">
                  <v-btn
                    icon
                    x-small
                    color="secondary"
                    class="ml-2"
                    v-bind="props"
                  >
                    <v-icon small>mdi-information-outline</v-icon>
                  </v-btn>
                </template>
                <span>Zeigt die Annuität je Maßnahme in Euro pro Jahr für alle drei Szenarien. Die Annuität verteilt den Kapitalwert gleichmäßig über die Laufzeit; höhere positive Werte stehen für bessere Wirtschaftlichkeit.</span>
              </v-tooltip>
              <v-menu>
                <template v-slot:activator="{ props }">
                  <v-btn
                    icon
                    color="primary"
                    v-bind="props"
                    title="Optionen"
                  >
                    <v-icon>mdi-dots-vertical</v-icon>
                  </v-btn>
                </template>
                <v-list density="compact">
                  <v-list-item @click="downloadChartImageByRef('valeriAnnuityChart', 'valeri-annuitaet')">
                    <template v-slot:prepend><v-icon>mdi-image</v-icon></template>
                    <v-list-item-title>Grafik herunterladen</v-list-item-title>
                  </v-list-item>
                  <v-list-item @click="exportChartDataExcelGeneric(annuityComparisonData, 'valeri-annuitaet')">
                    <template v-slot:prepend><v-icon>mdi-file-excel</v-icon></template>
                    <v-list-item-title>Daten exportieren</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </div>
            <div class="chart-wrapper">
              <standard-bar-chart
                ref="valeriAnnuityChart"
                :chart-data="annuityComparisonData"
                :options="annuityComparisonOptions"
              />
            </div>
              </v-card>
            </v-col>

            <v-col
              v-if="hasSensitivityRadarData"
              cols="12"
              :md="hasAnnuityComparisonData ? 4 : 12"
              class="pl-md-2"
              :class="{ 'mt-4 mt-md-0': hasAnnuityComparisonData }"
            >
              <v-card class="chart-section pa-4" variant="outlined">
            <div class="chart-title d-flex align-center">
              <span class="chart-title-text">Sensitivität der Szenarioparameter</span>
              <v-spacer />
              <v-tooltip location="bottom" max-width="420">
                <template v-slot:activator="{ props }">
                  <v-btn
                    icon
                    x-small
                    color="secondary"
                    class="ml-2"
                    v-bind="props"
                  >
                    <v-icon small>mdi-information-outline</v-icon>
                  </v-btn>
                </template>
                <span>Zeigt die Szenarioparameter, auf den wahrscheinlichen Fall als 100 Prozent normiert. Weiter außen heißt nur „größerer Parameterwert“, nicht „besser“: ein höherer Kalkulationszinssatz, höhere Betriebskosten und höhere Auszahlungen senken den Kapitalwert, höhere Einzahlungen heben ihn. Das Diagramm zeigt die Spreizung der Annahmen, nicht deren Wirkung.</span>
              </v-tooltip>
              <v-menu>
                <template v-slot:activator="{ props }">
                  <v-btn
                    icon
                    color="primary"
                    v-bind="props"
                    title="Optionen"
                  >
                    <v-icon>mdi-dots-vertical</v-icon>
                  </v-btn>
                </template>
                <v-list density="compact">
                  <v-list-item @click="downloadChartImageByRef('valeriRadarChart', 'valeri-radar')">
                    <template v-slot:prepend><v-icon>mdi-image</v-icon></template>
                    <v-list-item-title>Grafik herunterladen</v-list-item-title>
                  </v-list-item>
                  <v-list-item @click="exportChartDataExcelGeneric(sensitivityRadarData, 'valeri-radar')">
                    <template v-slot:prepend><v-icon>mdi-file-excel</v-icon></template>
                    <v-list-item-title>Daten exportieren</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </div>
            <div class="chart-wrapper radar-wrapper">
              <radar-chart
                ref="valeriRadarChart"
                :chart-data="sensitivityRadarData"
                :options="sensitivityRadarOptions"
              />
            </div>
              </v-card>
            </v-col>
          </v-row>

          <v-card v-if="hasIrrComparisonData" class="chart-section pa-4 mt-4" variant="outlined">
            <div class="chart-title d-flex align-center">
              <span class="chart-title-text">Interner Zinsfuß (IRR) je Maßnahme</span>
              <v-spacer />
              <v-tooltip location="bottom" max-width="420">
                <template v-slot:activator="{ props }">
                  <v-btn
                    icon
                    x-small
                    color="secondary"
                    class="ml-2"
                    v-bind="props"
                  >
                    <v-icon small>mdi-information-outline</v-icon>
                  </v-btn>
                </template>
                <span>Zeigt den internen Zinsfuß je Maßnahme in Prozent. Der IRR ist der Zinssatz, bei dem der Kapitalwert null wird; ein höherer IRR steht für eine attraktivere Investition.</span>
              </v-tooltip>
              <v-menu>
                <template v-slot:activator="{ props }">
                  <v-btn
                    icon
                    color="primary"
                    v-bind="props"
                    title="Optionen"
                  >
                    <v-icon>mdi-dots-vertical</v-icon>
                  </v-btn>
                </template>
                <v-list density="compact">
                  <v-list-item @click="downloadChartImageByRef('valeriIrrChart', 'valeri-irr')">
                    <template v-slot:prepend><v-icon>mdi-image</v-icon></template>
                    <v-list-item-title>Grafik herunterladen</v-list-item-title>
                  </v-list-item>
                  <v-list-item @click="exportChartDataExcelGeneric(irrComparisonData, 'valeri-irr')">
                    <template v-slot:prepend><v-icon>mdi-file-excel</v-icon></template>
                    <v-list-item-title>Daten exportieren</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </div>
            <div class="chart-wrapper">
              <standard-bar-chart
                ref="valeriIrrChart"
                :chart-data="irrComparisonData"
                :options="irrComparisonOptions"
              />
            </div>
          </v-card>
        </div>
        <v-alert v-else type="info" outlined dense>
          Keine Grafikdaten vorhanden.
        </v-alert>
      </div>
    </div>
  </div>
</template>

<script>
import { Chart, registerables } from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import LineChart from "@/components/calcpreview/LineChart.vue";
import HorizontalBarChart from "@/components/calcpreview/HorizontalBarChart.vue";
import RadarChart from "@/components/calcpreview/RadarChart.vue";
import StandardBarChart from "@/components/calcpreview/StandardBarChart.vue";
import HelpBar from "@/components/HelpBar.vue";
import ProjectPageNav from "@/components/ProjectPageNav.vue";
import ValeriHelp from "@/components/help/ValeriHelp.vue";
import { useProjectStore } from "@/stores/projectStore";
import { datalabelsGuard } from "@/components/calcpreview/datalabelsGuard";
import { storeDocxModuleCharts, getDocxCombinedCharts } from "@/utils/docxAppendixCache";
import { exportChartExcel } from "@/utils/chartDataExport";
import api from "@/plugins/axios";
import { toast } from "vue3-toastify";
import { fragenObVerlassen } from '@/composables/useUnsavedChanges';

/*
 * `ChartDataLabels` gilt ab hier für **jedes** Diagramm der Seite. Nur die
 * Kapitalwertkurve will Beschriftungen; die übrigen sechs schalten das Plugin
 * in ihren Optionen mit `datalabels: false` ab.
 *
 * `false` und nicht `{ display: false }`: bei `{ display: false }` bleibt das
 * Plugin dem Diagramm zugeschaltet und baut für jeden Datenpunkt ein
 * Label-Objekt, das niemand zeichnet. Mit `false` verwirft Chart.js den
 * Plugin-Deskriptor für dieses Diagramm (`getOpts`: `options === false` ->
 * `null`) und es läuft kein einziger Hook.
 *
 * Das ist eine Ersparnis, **kein** Schutz vor dem Absturz
 *
 *     Cannot read properties of undefined (reading 'length')
 *     chartjs-plugin-datalabels.esm.js:1003
 *
 * — eine frühere Notiz an dieser Stelle behauptete das und lag falsch. Der
 * Absturz trifft jedes Diagramm, das das Plugin wirklich nutzt, hier also die
 * Kapitalwertkurve, und er hängt nicht daran, ob der Canvas eingehängt ist.
 * Hergeleitet und abgesichert wird er in `datalabelsGuard.js`; `Chart.register`
 * nimmt die Absicherung unten mit auf.
 */
Chart.register(...registerables, ChartDataLabels, datalabelsGuard);

const SCENARIO_LABELS = {
  probable: "Wahrscheinlich",
  worstCase: "Worst-Case",
  bestCase: "Best-Case",
};

const SCENARIO_COLORS = {
  probable: "#d4aa00",
  worstCase: "#ef8f84",
  bestCase: "#7bc043",
};

//: Die `ref`-Namen der sieben Diagramme, in der Reihenfolge der Seite.
const VALERI_CHART_REFS = [
  "valeriScenarioChart",
  "valeriTornadoChart",
  "valeriCashflowChart",
  "valeriPaybackChart",
  "valeriAnnuityChart",
  "valeriRadarChart",
  "valeriIrrChart",
];

const SENSITIVITY_AXES = [
  { key: "discountRatePercent", label: "Kalkulationszins" },
  { key: "energyPriceEscalationPercent", label: "Energiepreis" },
  { key: "operationCostEscalationPercent", label: "Betriebskosten" },
  { key: "degradationFactorPercent", label: "Degradation" },
  { key: "inflowFactorPercent", label: "Einzahlungen" },
  { key: "outflowFactorPercent", label: "Auszahlungen" },
];

/**
 * Eine optionale Zahl aus einem Eingabefeld.
 *
 * Der Unterschied zwischen „leer" und „null Prozent" trägt hier eine Bedeutung:
 * leer heisst, dass der Kalkulationszinssatz gesetzt bleibt; 0 wäre ein
 * Zinssatz von null. `Number('')` und `Number(null)` ergeben beide 0 und
 * verwischen genau das.
 *
 * @param {unknown} wert Rohwert aus dem Feld oder von der API.
 * @returns {number|null} Die Zahl, oder `null`, wenn nichts angegeben ist.
 */
const toZahlOderNull = (wert) => {
  if (wert === null || wert === undefined || wert === "") return null;
  const zahl = Number(wert);
  return Number.isFinite(zahl) ? zahl : null;
};

const defaultScenarioSettings = () => ({
  probable: {
    analysisYears: 15,
    discountRatePercent: 3.5,
    energyPriceEscalationPercent: 2,
    operationCostEscalationPercent: 2,
    defaultUsefulLifeYears: 15,
    inflowFactorPercent: 100,
    outflowFactorPercent: 100,
    degradationFactorPercent: 100,
    equityRatePercent: null,
    debtRatePercent: null,
  },
  worstCase: {
    analysisYears: 10,
    discountRatePercent: 4.5,
    energyPriceEscalationPercent: 1.5,
    operationCostEscalationPercent: 3,
    defaultUsefulLifeYears: 10,
    inflowFactorPercent: 75,
    outflowFactorPercent: 125,
    degradationFactorPercent: 125,
    equityRatePercent: null,
    debtRatePercent: null,
  },
  bestCase: {
    analysisYears: 20,
    discountRatePercent: 2.5,
    energyPriceEscalationPercent: 4.5,
    operationCostEscalationPercent: 1.5,
    defaultUsefulLifeYears: 20,
    inflowFactorPercent: 125,
    outflowFactorPercent: 75,
    degradationFactorPercent: 75,
    equityRatePercent: null,
    debtRatePercent: null,
  },
});

export default {
  name: "ProjectCalcValeri",
  setup() {
    const store = useProjectStore();
    return { store };
  },
  components: {
    LineChart,
    HorizontalBarChart,
    RadarChart,
    StandardBarChart,
    HelpBar,
    ProjectPageNav,
  },
  data() {
    return {
      /*
       * Der zuletzt gespeicherte Parameterstand, serialisiert. `null`, solange
       * noch nichts geladen wurde — dann gilt nichts als geändert.
       */
      gemerkterStand: null,
      // Höhe des Diagrammfelds in Pixeln.
      //
      // Muss mit `min-height` von `.chart-wrapper` im Style-Block dieser Datei
      // übereinstimmen: die Balken- und Netzdiagramme bringen keinen eigenen
      // Wrapper mit und folgen dieser CSS-Angabe, LineChart bringt einen mit
      // und bekommt die Höhe als Eigenschaft. Laufen die beiden Zahlen
      // auseinander, sind die Karten einer Reihe verschieden hoch.
      CHART_HEIGHT: 420,
      scenarioResponses: {},
      errorResponse: null,
      methodTexts: null,
      ValeriHelp,
      loading: false,
      isExporting: false,
      isDocxExporting: false,
      isDinExporting: false,
      actionCalcList: [],
      usefulLifeYears: {},
      activeScenario: "probable",
      showGroupDetails: false,
      collapsedGroupsByScenario: {
        probable: [],
        worstCase: [],
        bestCase: [],
      },
      scenarioSettings: defaultScenarioSettings(),
      /*
       * Die Eingaben, mit denen zuletzt wirklich gerechnet wurde.
       *
       * Gerechnet wird nur beim Aufbau der Seite und nach „Werte Speichern".
       * Drei der sieben Grafiken lasen ihre Werte aber direkt aus
       * `scenarioSettings` — das Netzdiagramm und der Zeithorizont der
       * Amortisation. Eine geänderte Zahl im Eingabefeld verschob also sofort
       * diese drei, während Tabellen und die übrigen vier Grafiken noch das
       * vorige Ergebnis zeigten. Die Seite widersprach sich, ohne es zu sagen.
       *
       * Jetzt lesen alle Darstellungen aus diesem Abbild, und ein Hinweis über
       * der Tabelle sagt, wenn Eingaben und Ergebnis auseinanderlaufen.
       */
      calculatedSettings: null,
      calculatedEquitySharePercent: null,
      parameterRows: [
        { key: "inflowFactorPercent", label: "Berücksichtigung Einzahlungsbeträge", suffix: "%", step: "0.1" },
        { key: "outflowFactorPercent", label: "Berücksichtigung Auszahlungsbeträge", suffix: "%", step: "0.1" },
        { key: "energyPriceEscalationPercent", label: "Jährliche Energiepreisschwankungen", suffix: "%/a", step: "0.1" },
        { key: "operationCostEscalationPercent", label: "Jährliche Preisschwankungsrate für Dienstleistungen und Materialien", suffix: "%/a", step: "0.1" },
        { key: "degradationFactorPercent", label: "Berücksichtigung Degradation", suffix: "%", step: "0.1" },
        { key: "analysisYears", label: "Laufzeit der Investition", suffix: "a", step: "1" },
        // Vorher hiess diese Zeile „Kalkulationszinssatz (WACC)". Das war
        // irrefuehrend: der Wert war frei gesetzt und gerade kein WACC. Die
        // Herleitung steht jetzt in den beiden Zeilen darunter.
        { key: "discountRatePercent", label: "Kalkulationszinssatz", suffix: "%", step: "0.1" },
        { key: "equityRatePercent", label: "Zinssatz Eigenkapital (für WACC)", suffix: "%", step: "0.1" },
        { key: "debtRatePercent", label: "Zinssatz Fremdkapital (für WACC)", suffix: "%", step: "0.1" },
        { key: "defaultUsefulLifeYears", label: "Nutzungsdauer Fallback", suffix: "a", step: "1" },
      ],
      //: Eigenkapitalanteil — projektweit, nicht je Szenario (so wie in der
      //: Vorlage). `null` heisst: keine Herleitung, es bleibt beim gesetzten
      //: Kalkulationszinssatz.
      equitySharePercent: null,
      chartOptions: {
        plugins: {
          datalabels: {
            display(context) {
              const values = context.dataset.data || [];
              let lastValidIndex = -1;
              for (let index = values.length - 1; index >= 0; index -= 1) {
                if (values[index] !== null && values[index] !== undefined) {
                  lastValidIndex = index;
                  break;
                }
              }
              const isLastPoint = context.dataIndex === lastValidIndex;
              const value = values[context.dataIndex];
              return isLastPoint && value !== null && value !== undefined;
            },
            formatter(value) {
              return Number(value).toLocaleString("de-DE", {
                maximumFractionDigits: 0,
              });
            },
            align: "top",
            anchor: "end",
            offset: 8,
            color(context) {
              return context.dataset.borderColor || "#000";
            },
            font: {
              weight: "bold",
            },
            backgroundColor: "rgba(255,255,255,0.85)",
            borderRadius: 4,
            padding: 4,
            clip: false,
            clamp: true,
          },
        },
        responsive: true,
        maintainAspectRatio: false,
        legend: {
          display: true,
          position: "bottom",
        },
        // Kein eigener Diagrammtitel: die Karte traegt denselben Text schon
        // in ihrer Kopfzeile, er stand also zweimal untereinander. Die uebrigen
        // Diagramme formulieren ihren Titel abweichend und behalten ihn als
        // Unterzeile.
        title: {
          display: false,
        },
        tooltips: {
          mode: "nearest",
          intersect: false,
          callbacks: {
            label(tooltipItem, data) {
              const dataset = data.datasets[tooltipItem.datasetIndex] || {};
              const value = Number(tooltipItem.yLabel);
              const formattedValue = new Intl.NumberFormat("de-DE", {
                maximumFractionDigits: 0,
              }).format(Number.isFinite(value) ? value : 0);
              const lines = [`${dataset.label}: ${formattedValue} €`];

              const values = dataset.data || [];
              let lastValidIndex = -1;
              for (let index = values.length - 1; index >= 0; index -= 1) {
                if (values[index] !== null && values[index] !== undefined) {
                  lastValidIndex = index;
                  break;
                }
              }

              const residualContribution = Number(dataset.residualContribution);
              if (
                tooltipItem.index === lastValidIndex
                && Number.isFinite(residualContribution)
                && residualContribution > 0
              ) {
                lines.push(
                  `davon Restwertanteil: ${new Intl.NumberFormat("de-DE", {
                    maximumFractionDigits: 0,
                  }).format(residualContribution)} €`
                );
              }

              return lines;
            },
          },
        },
        scales: {
          xAxes: [
            {
              scaleLabel: {
                display: true,
                labelString: "Jahre",
              },
            },
          ],
          yAxes: [
            {
              ticks: {
                callback(value) {
                  return new Intl.NumberFormat("de-DE").format(value);
                },
              },
              gridLines: {
                zeroLineColor: "rgba(0,0,0,0.6)",
                zeroLineWidth: 2,
              },
              scaleLabel: {
                display: true,
                labelString: "Kapitalwert [€]",
              },
            },
          ],
        },
      },
    };
  },
  computed: {
    projectId() {
      return this.$route.params.projectId;
    },
    project() {
      return this.store.currentProject;
    },
    actionList() {
      return this.store.currentProjectActions || [];
    },
    currentScenarioResponse() {
      return this.scenarioResponses[this.activeScenario] || null;
    },
    currentActionResults() {
      return this.currentScenarioResponse?.result?.actions || [];
    },
    currentActionGroupResults() {
      return this.currentScenarioResponse?.result?.actionGroups || [];
    },
    currentTotalResult() {
      return this.currentScenarioResponse?.result?.total || null;
    },
    currentWarnings() {
      return this.currentScenarioResponse?.result?.warnings || [];
    },
    /*
     * `calculate_project_valeri` liefert neben `warnings` auch `errors`
     * (projects/valeri.py). Diese Ansicht hat die Liste bisher weggeworfen und
     * nur die Warnungen gezeigt — zusammen mit der stillen „0" in der Tabelle
     * blieb eine durchgefallene Maßnahme damit völlig unsichtbar.
     */
    currentErrors() {
      return this.currentScenarioResponse?.result?.errors || [];
    },
    /*
     * Anhang 5.1 der VALERI-Vorlage: r = S_eq · r_eq + S_debt · r_debt.
     *
     * Hier nur zur Anzeige — gerechnet wird mit dem Wert, den der Server
     * herleitet. Beide Formeln stehen bewusst getrennt: der Hinweis soll
     * zeigen, was herauskommt, und nicht zur zweiten Wahrheit werden.
     */
    waccPercent() {
      // Über `toZahlOderNull` und nicht über `Number`: ein leeres Feld muss
      // `null` bleiben. Ein Test auf `!== ""` nach der Umwandlung liefe ins
      // Leere, weil `Number('')` bereits 0 ist.
      const anteil = toZahlOderNull(this.equitySharePercent);
      const eigen = toZahlOderNull(this.scenarioSettings.probable.equityRatePercent);
      const fremd = toZahlOderNull(this.scenarioSettings.probable.debtRatePercent);
      if (anteil === null || eigen === null || fremd === null) return null;
      if (anteil < 0 || anteil > 100) return null;
      return (anteil / 100) * eigen + (1 - anteil / 100) * fremd;
    },
    equityShareError() {
      const wert = this.equitySharePercent;
      if (wert === null || wert === "" || wert === undefined) return "";
      if (!Number.isFinite(Number(wert))) return "Bitte eine Zahl angeben.";
      if (Number(wert) < 0 || Number(wert) > 100) return "Der Anteil muss zwischen 0 und 100 % liegen.";
      return "";
    },
    /*
     * Was die letzte Rechnung wirklich verwendet hat — nicht, was die Seite
     * sich ausrechnet.
     *
     * Der Server legt seine normierten Einstellungen neben das Ergebnis
     * (`calculate_project_valeri` gibt `{settings, result}` zurück) und
     * vermerkt in `discountRateFromWacc`, ob die Herleitung den Zinssatz
     * bestimmt hat. Diese Ansicht hat das Feld bisher weggeworfen und sich auf
     * die eigene Formel verlassen — womit der Hinweis auch dann von einer
     * Herleitung sprach, wenn gar keine stattgefunden hatte.
     */
    effectiveProbableSettings() {
      return this.scenarioResponses.probable?.settings || null;
    },
    /*
     * Laufen Eingaben und letztes Ergebnis auseinander?
     *
     * Verglichen wird gegen das Abbild aus `calculatedSettings`. Solange noch
     * nie gerechnet wurde, gilt die Seite nicht als geändert — sonst stünde
     * der Hinweis schon beim Aufbau da.
     */
    settingsDirty() {
      if (!this.calculatedSettings) return false;
      return (
        JSON.stringify(this.scenarioSettings) !== JSON.stringify(this.calculatedSettings)
        || toZahlOderNull(this.equitySharePercent) !== this.calculatedEquitySharePercent
      );
    },
    waccHinweis() {
      const gerechnet = this.effectiveProbableSettings;
      if (gerechnet?.discountRateFromWacc) {
        const zins = Number(gerechnet.discountRatePercent);
        const anteil = Number(gerechnet.equitySharePercent);
        return (
          `Kalkulationszinssatz aus Eigen- und Fremdkapital hergeleitet und so ` +
          `gerechnet: ${zins.toFixed(2).replace(".", ",")} % ` +
          `(${anteil} % Eigenkapital, ${100 - anteil} % Fremdkapital). ` +
          `Der Wert in der Zeile „Kalkulationszinssatz" ist damit überschrieben; ` +
          `Anhang 5.1 des Bewertungsberichts zeigt dieselbe Rechnung.`
        );
      }
      if (this.waccPercent !== null) {
        return (
          `Herleitung vorbereitet: ${this.waccPercent.toFixed(2).replace(".", ",")} %. ` +
          `Sie gilt erst für die nächste Berechnung — bis dahin steht in den ` +
          `Tabellen und Grafiken der zuletzt gerechnete Zinssatz.`
        );
      }
      return (
        "Leer lassen, wenn der Kalkulationszinssatz direkt gesetzt werden soll. " +
        "Sind Eigenkapitalanteil und beide Zinssätze gefüllt, wird er daraus " +
        "hergeleitet (WACC) und Anhang 5.1 des Bewertungsberichts zeigt die Rechnung."
      );
    },
    scenarioActionsByKey() {
      const build = (scenarioKey) => {
        const response = this.scenarioResponses[scenarioKey]?.result || {};
        const directActions = (response.actions || []).map((action) => ({
          ...action,
          id: this.scenarioEntityId("action", action.id),
          _sourceId: action.id,
          _entityType: "action",
        }));
        const groupEntries = (response.actionGroups || []).map((group) => ({
          ...group,
          id: this.scenarioEntityId("group", group.id),
          _sourceId: group.id,
          _entityType: "group",
        }));

        if (!this.showGroupDetails) {
          return [...groupEntries, ...directActions];
        }

        const groupedActions = (response.actionGroups || [])
          .flatMap((group) => group.actions || [])
          .map((action) => ({
            ...action,
            id: this.scenarioEntityId("group-action", action.id),
            _sourceId: action.id,
            _entityType: "group-action",
          }));

        /*
         * Gruppeneinträge **vor** den Einzelmaßnahmen, genau wie im Zweig
         * darüber. Vorher stand hier `[...directActions, ...groupedActions]`:
         * die Reihenfolge kippte beim Umschalten, und weil zugleich der
         * Namensraum der Kennungen wechselt (`group-<id>` zu
         * `group-action-<id>`), vertauschten alle sieben Grafiken ihre
         * Kategorien und Farben. Ein Schalter, der „Details anzeigen" heisst,
         * soll Gruppen aufklappen und nicht die Reihenfolge umwerfen.
         */
        return [...groupedActions, ...directActions];
      };

      return {
        probable: build("probable"),
        worstCase: build("worstCase"),
        bestCase: build("bestCase"),
      };
    },
    allActionIds() {
      const ids = new Set();
      ["probable", "worstCase", "bestCase"].forEach((key) => {
        this.scenarioActionsByKey[key].forEach((action) => {
          if (action?.id !== undefined && action?.id !== null) {
            ids.add(action.id);
          }
        });
      });
      return Array.from(ids);
    },
    /*
     * Die Beschriftungen müssen eindeutig sein.
     *
     * Sie bilden die Kategorieachse von fünf Grafiken, und das Tornado-
     * Diagramm benutzt die Beschriftung zusätzlich als `y` seiner
     * Markierungspunkte. Zwei Maßnahmen gleichen Namens — „Pumpe" ist schnell
     * zweimal vergeben — fielen damit auf dieselbe Kategorie, und die
     * Markierung landete auf der falschen Zeile. Gleiche Namen werden deshalb
     * durchnummeriert.
     */
    allActionLabels() {
      const vergeben = new Map();
      return this.allActionIds.map((id) => {
        const probableAction = this.scenarioActionsByKey.probable.find((action) => action.id === id);
        const worstAction = this.scenarioActionsByKey.worstCase.find((action) => action.id === id);
        const bestAction = this.scenarioActionsByKey.bestCase.find((action) => action.id === id);
        const name = probableAction?.name || worstAction?.name || bestAction?.name || `Maßnahme ${id}`;
        const bisher = vergeben.get(name) || 0;
        vergeben.set(name, bisher + 1);
        return bisher === 0 ? name : `${name} (${bisher + 1})`;
      });
    },
    hasAnyVisualizationData() {
      return (
        this.hasChartData ||
        this.hasNpvTornadoData ||
        this.hasActiveCashflowStackData ||
        this.hasPaybackTimelineData ||
        this.hasAnnuityComparisonData ||
        this.hasSensitivityRadarData ||
        this.hasIrrComparisonData
      );
    },
    hasChartData() {
      return Object.values(this.scenarioResponses).some(
        (response) => (response?.result?.total?.cashflows || []).length > 0
      );
    },
    /*
     * Die drei Schalter hiessen schlicht `allActionIds.length > 0` — sie
     * fragten, ob es Maßnahmen gibt, nicht ob es Zahlen zu zeigen gibt. Vor
     * der ersten Rechnung ist die Liste zwar leer, nach einer fehlgeschlagenen
     * aber nicht unbedingt, und dann stand eine Grafik aus lauter Nullen da.
     */
    hasNpvTornadoData() {
      return this.allActionIds.length > 0
        && ["probable", "worstCase", "bestCase"].some((key) => this.scenarioResponses[key]);
    },
    npvTornadoData() {
      const labels = this.allActionLabels;
      const ranges = [];
      const probableMarkers = [];

      this.allActionIds.forEach((id, index) => {
        const probable = Number(this.findActionForScenario("probable", id)?.capitalValue);
        const worst = Number(this.findActionForScenario("worstCase", id)?.capitalValue);
        const best = Number(this.findActionForScenario("bestCase", id)?.capitalValue);
        const values = [worst, best].filter((value) => Number.isFinite(value));
        if (values.length === 0) {
          /*
           * `null` statt `[0, 0]`: ein Nullbalken mit Markierung bei 0 war von
           * einer Maßnahme mit tatsächlich null Euro Kapitalwert nicht zu
           * unterscheiden. Fehlt die Maßnahme in Worst- und Best-Case, gibt es
           * keine Spanne — dann soll dort auch nichts stehen.
           */
          ranges.push(null);
          probableMarkers.push({ x: null, y: labels[index] });
          return;
        }
        const low = Math.min(...values);
        const high = Math.max(...values);
        ranges.push([low, high]);
        probableMarkers.push({
          x: Number.isFinite(probable) ? probable : low,
          y: labels[index],
        });
      });

      return {
        labels,
        datasets: [
          {
            label: "Szenario-Spanne (Worst/Best)",
            backgroundColor: "rgba(21, 101, 192, 0.35)",
            borderColor: "#1565c0",
            borderWidth: 1,
            data: ranges,
          },
          {
            label: "Wahrscheinlich",
            type: "line",
            data: probableMarkers,
            showLine: false,
            fill: false,
            pointRadius: 4,
            pointHoverRadius: 5,
            pointBackgroundColor: SCENARIO_COLORS.probable,
            pointBorderColor: "#5d4037",
            borderColor: "rgba(0,0,0,0)",
          },
        ],
      };
    },
    npvTornadoOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        legend: {
          display: true,
          position: "bottom",
        },
        title: {
          display: true,
          text: "Kapitalwert-Spanne je Maßnahme",
          fontSize: 16,
        },
        plugins: {
          // `false` statt `{ display: false }`: das Plugin wird für dieses
          // Diagramm gar nicht erst zugeschaltet. Siehe die Notiz am
          // Plugin-Register oben — mit `{ display: false }` bleibt es aktiv
          // und baut für jeden Datenpunkt ein Label-Objekt, das niemand
          // zeichnet.
          datalabels: false,
        },
        tooltips: {
          mode: "nearest",
          intersect: false,
          callbacks: {
            label(tooltipItem, data) {
              const dataset = data.datasets[tooltipItem.datasetIndex];
              if (dataset.type === "line") {
                return `${dataset.label}: ${new Intl.NumberFormat("de-DE", {
                  maximumFractionDigits: 0,
                }).format(tooltipItem.xLabel)} €`;
              }
              const pair = dataset.data[tooltipItem.index];
              if (!Array.isArray(pair)) {
                // Siehe `npvTornadoData`: ohne Worst- und Best-Case gibt es
                // keine Spanne. Vorher stand hier durch `|| [0, 0]`
                // „0 € bis 0 €".
                return `${dataset.label}: keine Spanne vorhanden`;
              }
              return `${dataset.label}: ${new Intl.NumberFormat("de-DE", {
                maximumFractionDigits: 0,
              }).format(pair[0])} € bis ${new Intl.NumberFormat("de-DE", {
                maximumFractionDigits: 0,
              }).format(pair[1])} €`;
            },
          },
        },
        scales: {
          xAxes: [
            {
              ticks: {
                callback(value) {
                  return new Intl.NumberFormat("de-DE", {
                    maximumFractionDigits: 0,
                  }).format(value);
                },
              },
              scaleLabel: {
                display: true,
                labelString: "Kapitalwert [€]",
              },
            },
          ],
          yAxes: [
            {
              ticks: {
                autoSkip: false,
              },
            },
          ],
        },
      };
    },
    hasActiveCashflowStackData() {
      return this.currentActionResults.some((action) => (action.cashflows || []).length > 0)
        || this.currentActionGroupResults.some((group) => (group.actions || []).some((action) => (action.cashflows || []).length > 0));
    },
    activeCashflowStackData() {
      const actions = this.getActionsForScenario(this.activeScenario);
      const allYears = actions
        .flatMap((action) => (action.cashflows || []).map((entry) => Number(entry.year)))
        .filter((year) => Number.isFinite(year));
      const yearLabels = Array.from(new Set(allYears)).sort((a, b) => a - b);
      const labels = yearLabels.map((year) => String(year));

      const datasets = actions.map((action, index) => {
        const yearMap = (action.cashflows || []).reduce((acc, item) => {
          const year = Number(item.year);
          acc[year] = Number(item.cashflow) || 0;
          return acc;
        }, {});
        const baseColor = this.pickSeriesColor(index);
        return {
          label: action.name || `Maßnahme ${action.id}`,
          data: yearLabels.map((year) => yearMap[year] || 0),
          /*
           * `fill: true` — die Achsen dieses Diagramms stehen auf `stacked`
           * und die Datenreihen trugen schon ein `stack`, aber mit
           * `fill: false` war von der Stapelung nichts zu sehen: gezeichnet
           * wurden nackte Linien auf aufsummierter Höhe. Damit liess sich
           * weder der Beitrag einer Maßnahme noch die Summe ablesen, was der
           * Titel beides verspricht. Die Füllfarbe stand längst bereit.
           */
          fill: true,
          // `tension` heisst es seit Chart.js 3; `lineTension` war aus v2 und
          // wurde stillschweigend verworfen.
          tension: 0,
          borderWidth: 1,
          borderColor: baseColor,
          backgroundColor: this.toRgba(baseColor, 0.25),
          pointRadius: 0,
          stack: "cashflow",
        };
      });

      return {
        labels,
        datasets,
      };
    },
    activeCashflowStackOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        legend: {
          display: true,
          position: "bottom",
        },
        title: {
          display: true,
          text: `Cashflow-Verlauf (${this.scenarioLabel(this.activeScenario)})`,
          fontSize: 16,
        },
        plugins: {
          // `false` statt `{ display: false }`: das Plugin wird für dieses
          // Diagramm gar nicht erst zugeschaltet. Siehe die Notiz am
          // Plugin-Register oben — mit `{ display: false }` bleibt es aktiv
          // und baut für jeden Datenpunkt ein Label-Objekt, das niemand
          // zeichnet.
          datalabels: false,
        },
        scales: {
          xAxes: [
            {
              stacked: true,
              scaleLabel: {
                display: true,
                labelString: "Jahr",
              },
            },
          ],
          yAxes: [
            {
              stacked: true,
              ticks: {
                callback(value) {
                  return new Intl.NumberFormat("de-DE", {
                    maximumFractionDigits: 0,
                  }).format(value);
                },
              },
              scaleLabel: {
                display: true,
                labelString: "Netto-Cashflow [€/a]",
              },
            },
          ],
        },
      };
    },
    hasPaybackTimelineData() {
      return this.allActionIds.length > 0
        && ["probable", "worstCase", "bestCase"].some((key) => this.scenarioResponses[key]);
    },
    paybackTimelineData() {
      const labels = this.allActionLabels;
      const scenarioKeys = ["probable", "worstCase", "bestCase"];
      const datasets = [];

      scenarioKeys.forEach((scenarioKey) => {
        const color = SCENARIO_COLORS[scenarioKey];
        const horizon = Number(this.calculatedScenario(scenarioKey).analysisYears) || 0;

        const paid = [];
        const noPayback = [];
        this.allActionIds.forEach((id) => {
          const payback = Number(this.findActionForScenario(scenarioKey, id)?.discountedPayback);
          if (Number.isFinite(payback)) {
            paid.push(payback);
            noPayback.push(null);
          } else {
            paid.push(null);
            noPayback.push(horizon);
          }
        });

        datasets.push({
          label: `${SCENARIO_LABELS[scenarioKey]} (erreicht)`,
          backgroundColor: this.toRgba(color, 0.7),
          borderColor: color,
          borderWidth: 1,
          data: paid,
        });

        datasets.push({
          label: `${SCENARIO_LABELS[scenarioKey]} (nicht erreicht)`,
          backgroundColor: this.toRgba(color, 0.15),
          borderColor: color,
          borderWidth: 2,
          borderDash: [6, 4],
          data: noPayback,
        });
      });

      return {
        labels,
        datasets,
      };
    },
    paybackTimelineOptions() {
      const maxHorizon = Math.max(
        Number(this.calculatedScenario("probable").analysisYears) || 0,
        Number(this.calculatedScenario("worstCase").analysisYears) || 0,
        Number(this.calculatedScenario("bestCase").analysisYears) || 0
      );

      return {
        responsive: true,
        maintainAspectRatio: false,
        legend: {
          display: true,
          position: "bottom",
        },
        title: {
          display: true,
          text: "Dynamische Amortisationszeit im Szenariovergleich",
          fontSize: 16,
        },
        plugins: {
          // `false` statt `{ display: false }`: das Plugin wird für dieses
          // Diagramm gar nicht erst zugeschaltet. Siehe die Notiz am
          // Plugin-Register oben — mit `{ display: false }` bleibt es aktiv
          // und baut für jeden Datenpunkt ein Label-Objekt, das niemand
          // zeichnet.
          datalabels: false,
        },
        scales: {
          xAxes: [
            {
              ticks: {
                min: 0,
                suggestedMax: maxHorizon,
              },
              scaleLabel: {
                display: true,
                labelString: "Jahre",
              },
            },
          ],
          yAxes: [
            {
              ticks: {
                autoSkip: false,
              },
            },
          ],
        },
      };
    },
    hasAnnuityComparisonData() {
      return this.annuityComparisonData.datasets.some((dataset) =>
        (dataset.data || []).some((value) => Number.isFinite(value) && value !== 0),
      );
    },
    annuityComparisonData() {
      const labels = this.allActionLabels;
      return {
        labels,
        datasets: [
          {
            label: SCENARIO_LABELS.probable,
            data: this.allActionIds.map((id) => Number(this.findActionForScenario("probable", id)?.annuity) || 0),
            backgroundColor: this.toRgba(SCENARIO_COLORS.probable, 0.7),
            borderColor: SCENARIO_COLORS.probable,
            borderWidth: 1,
          },
          {
            label: SCENARIO_LABELS.worstCase,
            data: this.allActionIds.map((id) => Number(this.findActionForScenario("worstCase", id)?.annuity) || 0),
            backgroundColor: this.toRgba(SCENARIO_COLORS.worstCase, 0.7),
            borderColor: SCENARIO_COLORS.worstCase,
            borderWidth: 1,
          },
          {
            label: SCENARIO_LABELS.bestCase,
            data: this.allActionIds.map((id) => Number(this.findActionForScenario("bestCase", id)?.annuity) || 0),
            backgroundColor: this.toRgba(SCENARIO_COLORS.bestCase, 0.7),
            borderColor: SCENARIO_COLORS.bestCase,
            borderWidth: 1,
          },
        ],
      };
    },
    annuityComparisonOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        legend: {
          display: true,
          position: "bottom",
        },
        title: {
          display: true,
          text: "Annuität im Szenariovergleich",
          fontSize: 16,
        },
        plugins: {
          // `false` statt `{ display: false }`: das Plugin wird für dieses
          // Diagramm gar nicht erst zugeschaltet. Siehe die Notiz am
          // Plugin-Register oben — mit `{ display: false }` bleibt es aktiv
          // und baut für jeden Datenpunkt ein Label-Objekt, das niemand
          // zeichnet.
          datalabels: false,
        },
        scales: {
          xAxes: [
            {
              ticks: {
                autoSkip: false,
              },
            },
          ],
          yAxes: [
            {
              ticks: {
                callback(value) {
                  return new Intl.NumberFormat("de-DE", {
                    maximumFractionDigits: 0,
                  }).format(value);
                },
              },
              scaleLabel: {
                display: true,
                labelString: "Annuität [€/a]",
              },
            },
          ],
        },
      };
    },
    /*
     * Vorher `Boolean(this.scenarioSettings?.probable)` — und `scenarioSettings`
     * ist von `defaultScenarioSettings()` vorbelegt, das also **immer** wahr.
     * Damit war auch `hasAnyVisualizationData` immer wahr: der Hinweis „Keine
     * Grafikdaten vorhanden." war unerreichbar, der Beobachter darauf konnte
     * nie auslösen, und die Seite zeigte schon vor der ersten Rechnung ein
     * Netzdiagramm aus reinen Eingabewerten.
     *
     * Das Netzdiagramm normiert auf den wahrscheinlichen Fall — dafür braucht
     * es ein Ergebnis, zu dem die Werte gehören.
     */
    hasSensitivityRadarData() {
      return Boolean(this.scenarioResponses.probable);
    },
    sensitivityRadarData() {
      const labels = SENSITIVITY_AXES.map((axis) => axis.label);
      const baseline = this.calculatedScenario("probable");

      return {
        labels,
        datasets: [
          "probable",
          "worstCase",
          "bestCase",
        ].map((scenarioKey) => {
          const scenario = this.calculatedScenario(scenarioKey);
          const color = SCENARIO_COLORS[scenarioKey];
          return {
            label: SCENARIO_LABELS[scenarioKey],
            data: SENSITIVITY_AXES.map((axis) => {
              /*
               * Ist der Bezugswert 0, gibt es kein „Prozent davon". Vorher
               * machte `|| 1` daraus stillschweigend eine Eins und blies die
               * Achse um das Hundertfache auf. `null` lässt die Achse leer.
               */
              const base = Number(baseline[axis.key]);
              const value = Number(scenario[axis.key]);
              if (!Number.isFinite(base) || base === 0 || !Number.isFinite(value)) {
                return null;
              }
              return (value / base) * 100;
            }),
            borderColor: color,
            backgroundColor: this.toRgba(color, 0.2),
            pointBackgroundColor: color,
            pointBorderColor: "#fff",
            borderWidth: 2,
          };
        }),
      };
    },
    sensitivityRadarOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        legend: {
          display: true,
          position: "bottom",
        },
        title: {
          display: true,
          text: "Normierte Parameter (Wahrscheinlich = 100%)",
          fontSize: 16,
        },
        plugins: {
          // `false` statt `{ display: false }`: das Plugin wird für dieses
          // Diagramm gar nicht erst zugeschaltet. Siehe die Notiz am
          // Plugin-Register oben — mit `{ display: false }` bleibt es aktiv
          // und baut für jeden Datenpunkt ein Label-Objekt, das niemand
          // zeichnet.
          datalabels: false,
        },
        scale: {
          ticks: {
            beginAtZero: true,
            suggestedMax: 150,
            callback(value) {
              return `${value}%`;
            },
          },
        },
      };
    },
    hasIrrComparisonData() {
      return this.irrComparisonData.datasets.some((dataset) =>
        (dataset.data || []).some((value) => Number.isFinite(value)),
      );
    },
    irrComparisonData() {
      const labels = this.allActionLabels;
      return {
        labels,
        datasets: [
          {
            label: SCENARIO_LABELS.probable,
            data: this.allActionIds.map((id) => this.getActionIrrPercent("probable", id)),
            backgroundColor: this.toRgba(SCENARIO_COLORS.probable, 0.7),
            borderColor: SCENARIO_COLORS.probable,
            borderWidth: 1,
          },
          {
            label: SCENARIO_LABELS.worstCase,
            data: this.allActionIds.map((id) => this.getActionIrrPercent("worstCase", id)),
            backgroundColor: this.toRgba(SCENARIO_COLORS.worstCase, 0.7),
            borderColor: SCENARIO_COLORS.worstCase,
            borderWidth: 1,
          },
          {
            label: SCENARIO_LABELS.bestCase,
            data: this.allActionIds.map((id) => this.getActionIrrPercent("bestCase", id)),
            backgroundColor: this.toRgba(SCENARIO_COLORS.bestCase, 0.7),
            borderColor: SCENARIO_COLORS.bestCase,
            borderWidth: 1,
          },
        ],
      };
    },
    irrComparisonOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        legend: {
          display: true,
          position: "bottom",
        },
        title: {
          display: true,
          text: "Interner Zinsfuß (IRR) im Szenariovergleich",
          fontSize: 16,
        },
        plugins: {
          // `false` statt `{ display: false }`: das Plugin wird für dieses
          // Diagramm gar nicht erst zugeschaltet. Siehe die Notiz am
          // Plugin-Register oben — mit `{ display: false }` bleibt es aktiv
          // und baut für jeden Datenpunkt ein Label-Objekt, das niemand
          // zeichnet.
          datalabels: false,
        },
        tooltips: {
          callbacks: {
            label(tooltipItem, data) {
              const dataset = data.datasets[tooltipItem.datasetIndex];
              const value = Number(tooltipItem.yLabel);
              if (!Number.isFinite(value)) {
                return `${dataset.label}: n/a`;
              }
              return `${dataset.label}: ${new Intl.NumberFormat("de-DE", {
                maximumFractionDigits: 2,
              }).format(value)} %`;
            },
          },
        },
        scales: {
          xAxes: [
            {
              ticks: {
                autoSkip: false,
              },
            },
          ],
          yAxes: [
            {
              ticks: {
                callback(value) {
                  return `${new Intl.NumberFormat("de-DE", {
                    maximumFractionDigits: 1,
                  }).format(value)} %`;
                },
              },
              scaleLabel: {
                display: true,
                labelString: "Interner Zinsfuß [%]",
              },
            },
          ],
        },
      };
    },
    scenarioChartData() {
      const allYears = ["probable", "worstCase", "bestCase"]
        .flatMap((key) => (this.scenarioResponses[key]?.result?.total?.cashflows || []).map((item) => Number(item.year)))
        .filter((year) => Number.isFinite(year));
      const yearLabels = Array.from(new Set(allYears)).sort((a, b) => a - b);
      const labels = yearLabels.map((year) => String(year));
      if (yearLabels.length > 0) {
        labels.push(String(yearLabels[yearLabels.length - 1] + 1));
      }

      return {
        labels,
        datasets: [
          {
            label: SCENARIO_LABELS.probable,
            borderColor: "#d4aa00",
            backgroundColor: "rgba(212,170,0,0.08)",
            fill: false,
            tension: 0,
            pointRadius: 2,
            borderWidth: 2,
            residualContribution: this.getDiscountedResidualContribution("probable"),
            data: this.buildScenarioSeries("probable", labels),
          },
          {
            label: SCENARIO_LABELS.worstCase,
            borderColor: "#ef8f84",
            backgroundColor: "rgba(239,143,132,0.08)",
            fill: false,
            tension: 0,
            pointRadius: 2,
            borderWidth: 2,
            residualContribution: this.getDiscountedResidualContribution("worstCase"),
            data: this.buildScenarioSeries("worstCase", labels),
          },
          {
            label: SCENARIO_LABELS.bestCase,
            borderColor: "#7bc043",
            backgroundColor: "rgba(123,192,67,0.08)",
            fill: false,
            tension: 0,
            pointRadius: 2,
            borderWidth: 2,
            residualContribution: this.getDiscountedResidualContribution("bestCase"),
            data: this.buildScenarioSeries("bestCase", labels),
          },
        ],
      };
    },
  },
  watch: {
    project: {
      handler(project) {
        if (project) {
          this.store.setTitle(project.name);
        }
      },
      immediate: true,
    },
    hasAnyVisualizationData(value) {
      if (value) {
        this.requestChartRedraw();
      }
    },
    /*
     * Der Schalter stand nur in `scenarioActionsByKey` und veränderte damit
     * allein die Grafiken — die Gruppendetails der Tabelle hingen
     * ausschliesslich am Klick auf eine Gruppenzeile. Beschriftet ist er aber
     * „Gruppendetails anzeigen", also muss er auch die Tabelle aufklappen.
     * Einzelne Zeilen bleiben danach weiter von Hand umschaltbar.
     */
    showGroupDetails(value) {
      const gruppenIds = (key) =>
        (this.scenarioResponses[key]?.result?.actionGroups || []).map((group) => group.id);
      this.collapsedGroupsByScenario = {
        probable: value ? [] : gruppenIds("probable"),
        worstCase: value ? [] : gruppenIds("worstCase"),
        bestCase: value ? [] : gruppenIds("bestCase"),
      };
    },
  },
  beforeMount() {
    this.fetchMethodTexts();
    /*
     * Mit `.catch`: ohne es blieb nach einem fehlgeschlagenen `fetch()` eine
     * leere Seite ohne jede Meldung stehen, und im Protokoll stand allein eine
     * unbehandelte Promise-Ablehnung. `fetchSettings` und `calcValeri` fangen
     * ihre Fehler selbst, der Projektabruf tat es nicht.
     */
    this.fetch()
      .then(() => this.fetchSettings())
      .then(() => this.merkeStand())
      .then(() => this.calcValeri())
      .catch((error) => {
        console.error("VALERI: Projekt konnte nicht geladen werden", error);
        this.errorResponse = "Das Projekt konnte nicht geladen werden.";
      });
  },
  mounted() {
    window.addEventListener("beforeunload", this.warnenBeimEntladen);
  },
  beforeUnmount() {
    window.removeEventListener("beforeunload", this.warnenBeimEntladen);

    // Sonst feuert der Neuaufbau noch nach dem Verlassen der Seite.
    if (this._redrawTimer) {
      window.clearTimeout(this._redrawTimer);
      this._redrawTimer = null;
    }
  },
  /*
   * Warnung vor ungespeicherten Parametern.
   *
   * Diese Seite ist in der Options-API geschrieben; `useUnsavedChanges` setzt
   * `<script setup>` voraus. Die Nachfrage selbst ist dieselbe — nur der Weg
   * dorthin ist der Router-Haken statt des Composables.
   *
   * Nicht zu verwechseln mit `settingsDirty`: das meldet, dass **neu zu rechnen**
   * ist. Hier geht es darum, dass die Parameter noch nicht im Projekt stehen.
   */
  async beforeRouteLeave() {
    if (!this.istGeaendert()) return true;
    return await fragenObVerlassen();
  },
  methods: {
    /** Den jetzigen Parameterstand als gespeichert vermerken. */
    merkeStand() {
      this.gemerkterStand = JSON.stringify(this.toApiSettingsPayload());
    },
    istGeaendert() {
      return (
        this.gemerkterStand !== null &&
        this.gemerkterStand !== JSON.stringify(this.toApiSettingsPayload())
      );
    },
    warnenBeimEntladen(ereignis) {
      if (!this.istGeaendert()) return;
      ereignis.preventDefault();
      ereignis.returnValue = "";
    },
    scenarioLabel(key) {
      return SCENARIO_LABELS[key] || key;
    },
    scenarioEntityId(entityType, id) {
      return `${entityType}-${id}`;
    },
    isScenarioGroupExpanded(scenarioKey, groupId) {
      return !(this.collapsedGroupsByScenario[scenarioKey] || []).includes(groupId);
    },
    toggleScenarioGroup(scenarioKey, groupId) {
      const current = [...(this.collapsedGroupsByScenario[scenarioKey] || [])];
      const index = current.indexOf(groupId);
      if (index > -1) {
        current.splice(index, 1);
      } else {
        current.push(groupId);
      }
      this.collapsedGroupsByScenario[scenarioKey] = current;
    },
    getActionsForScenario(scenarioKey) {
      return this.scenarioActionsByKey[scenarioKey] || [];
    },
    /*
     * Die Einstellungen eines Szenarios, so wie sie der letzten Rechnung
     * zugrunde lagen. Darstellungen müssen hierüber gehen und nicht über
     * `scenarioSettings`, sonst zeigen sie Zahlen, zu denen kein Ergebnis
     * gehört.
     *
     * Bevorzugt die normierten Werte des Servers — nur sie kennen den aus dem
     * WACC hergeleiteten Zinssatz. Fällt zurück auf das lokale Abbild und, vor
     * der ersten Rechnung, auf die Eingaben selbst.
     */
    calculatedScenario(scenarioKey) {
      return this.scenarioResponses[scenarioKey]?.settings
        || this.calculatedSettings?.[scenarioKey]
        || this.scenarioSettings[scenarioKey]
        || {};
    },
    findActionForScenario(scenarioKey, actionId) {
      return this.getActionsForScenario(scenarioKey).find((action) => action.id === actionId) || null;
    },
    getActionIrrPercent(scenarioKey, actionId) {
      const action = this.findActionForScenario(scenarioKey, actionId);
      if (!action) {
        return null;
      }

      const annualCashflows = this.extractAnnualCashflows(action);
      const irr = this.calculateIrr(annualCashflows);
      if (!Number.isFinite(irr)) {
        return null;
      }

      return irr * 100;
    },
    extractAnnualCashflows(action) {
      const cashflowMap = new Map();

      (action.cashflows || []).forEach((entry) => {
        const year = Number(entry.year);
        const cashflow = Number(entry.cashflow);
        if (!Number.isFinite(year) || !Number.isFinite(cashflow)) {
          return;
        }
        cashflowMap.set(year, (cashflowMap.get(year) || 0) + cashflow);
      });

      if (!cashflowMap.has(0)) {
        const invest = Number(action.invest);
        if (Number.isFinite(invest) && invest > 0) {
          cashflowMap.set(0, -invest);
        }
      }

      return Array.from(cashflowMap.entries())
        .map(([year, cashflow]) => ({ year, cashflow }))
        .sort((a, b) => a.year - b.year);
    },
    calculateIrr(cashflows) {
      if (!Array.isArray(cashflows) || cashflows.length === 0) {
        return null;
      }

      const npv = (rate) => cashflows.reduce((sum, entry) => {
        const denominator = (1 + rate) ** entry.year;
        if (!Number.isFinite(denominator) || denominator === 0) {
          return sum;
        }
        return sum + (entry.cashflow / denominator);
      }, 0);

      const hasPositive = cashflows.some((entry) => entry.cashflow > 0);
      const hasNegative = cashflows.some((entry) => entry.cashflow < 0);
      if (!hasPositive || !hasNegative) {
        return null;
      }

      let low = -0.95;
      let high = 1;
      let fLow = npv(low);
      let fHigh = npv(high);

      if (!Number.isFinite(fLow) || !Number.isFinite(fHigh)) {
        return null;
      }

      let guard = 0;
      while (fLow * fHigh > 0 && high < 50 && guard < 25) {
        high = (high + 1) * 2;
        fHigh = npv(high);
        guard += 1;
        if (!Number.isFinite(fHigh)) {
          return null;
        }
      }

      if (fLow === 0) {
        return low;
      }
      if (fHigh === 0) {
        return high;
      }
      if (fLow * fHigh > 0) {
        return null;
      }

      for (let i = 0; i < 80; i += 1) {
        const mid = (low + high) / 2;
        const fMid = npv(mid);
        if (!Number.isFinite(fMid)) {
          return null;
        }
        if (Math.abs(fMid) < 1e-8) {
          return mid;
        }
        if (fLow * fMid < 0) {
          high = mid;
          fHigh = fMid;
        } else {
          low = mid;
          fLow = fMid;
        }
      }

      return (low + high) / 2;
    },
    pickSeriesColor(index) {
      const palette = [
        "#1565c0",
        "#2e7d32",
        "#6a1b9a",
        "#ef6c00",
        "#00838f",
        "#ad1457",
        "#5d4037",
        "#424242",
      ];
      return palette[index % palette.length];
    },
    toRgba(hexColor, alpha) {
      const sanitized = (hexColor || "").replace("#", "");
      if (sanitized.length !== 6) {
        return `rgba(66,66,66,${alpha})`;
      }
      const red = parseInt(sanitized.substring(0, 2), 16);
      const green = parseInt(sanitized.substring(2, 4), 16);
      const blue = parseInt(sanitized.substring(4, 6), 16);
      return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
    },
    fetch() {
      return this.store.fetchCurrentProject(this.projectId);
    },
    fetchMethodTexts() {
      api
        .get("/method_text/")
        .then((response) => {
          this.methodTexts = response.data;
        })
        // Die Methodennamen sind Beschriftung, kein Ergebnis: `getMethodName`
        // fällt auf den Typschlüssel zurück. Ein Fehler darf die Seite also
        // nicht aufhalten, aber auch nicht als unbehandelte Ablehnung enden.
        .catch((error) => {
          console.warn("VALERI: Methodentexte nicht geladen", error);
        });
    },
    normalizeScenarioSettings(rawScenarioSettings) {
      const defaults = defaultScenarioSettings();
      return {
        probable: { ...defaults.probable, ...(rawScenarioSettings?.probable || {}) },
        worstCase: { ...defaults.worstCase, ...(rawScenarioSettings?.worstCase || {}) },
        bestCase: { ...defaults.bestCase, ...(rawScenarioSettings?.bestCase || {}) },
      };
    },
    fetchSettings() {
      return api
        .get(`/projects/${this.projectId}/valeri_settings/`)
        .then((response) => {
          const data = response.data || {};
          this.scenarioSettings = this.normalizeScenarioSettings(data.scenario_settings);
          /*
           * `??` statt `||`, und das ist hier kein Geschmack.
           *
           * Mit `||` fiel eine gespeicherte **0** auf den Vorgabewert zurück,
           * weil 0 unwahr ist. Damit liessen sich „0 % Energiepreissteigerung",
           * „0 % Degradation" oder „0 % Auszahlungen" nicht ablegen: speichern,
           * neu laden, und es standen wieder 2 bzw. 100 da. Genau diese Null
           * ist aber eine verbreitete Annahme — die Rechnung ohne Preis-
           * steigerung.
           *
           * Die beiden WACC-Zinssätze unten machten es schon richtig; die acht
           * Felder hier blieben zurück.
           */
          const gespeichert = (wert, rueckfall) => {
            const zahl = toZahlOderNull(wert);
            return zahl === null ? Number(rueckfall) : zahl;
          };
          const vorher = this.scenarioSettings.probable;
          this.scenarioSettings.probable = {
            ...vorher,
            analysisYears: gespeichert(data.analysis_years, vorher.analysisYears),
            discountRatePercent: gespeichert(data.discount_rate_percent, vorher.discountRatePercent),
            energyPriceEscalationPercent: gespeichert(
              data.energy_price_escalation_percent, vorher.energyPriceEscalationPercent
            ),
            operationCostEscalationPercent: gespeichert(
              data.operation_cost_escalation_percent, vorher.operationCostEscalationPercent
            ),
            defaultUsefulLifeYears: gespeichert(
              data.default_useful_life_years, vorher.defaultUsefulLifeYears
            ),
            inflowFactorPercent: gespeichert(data.inflow_factor_percent, vorher.inflowFactorPercent),
            outflowFactorPercent: gespeichert(data.outflow_factor_percent, vorher.outflowFactorPercent),
            degradationFactorPercent: gespeichert(
              data.degradation_factor_percent, vorher.degradationFactorPercent
            ),
            // Die beiden WACC-Zinssätze dürfen leer bleiben. `Number(null)`
            // wäre 0 — und 0 % Eigenkapitalzins ist eine Aussage, kein
            // „nicht gesetzt".
            equityRatePercent: toZahlOderNull(
              data.equity_rate_percent ?? vorher.equityRatePercent
            ),
            debtRatePercent: toZahlOderNull(
              data.debt_rate_percent ?? vorher.debtRatePercent
            ),
          };
          this.equitySharePercent = toZahlOderNull(data.equity_share_percent);
        })
        .catch(() => {
          toast.warning("VALERI-Standardwerte konnten nicht geladen werden.");
        });
    },
    toApiSettingsPayload() {
      return {
        analysis_years: Number(this.scenarioSettings.probable.analysisYears),
        discount_rate_percent: Number(this.scenarioSettings.probable.discountRatePercent),
        energy_price_escalation_percent: Number(this.scenarioSettings.probable.energyPriceEscalationPercent),
        operation_cost_escalation_percent: Number(this.scenarioSettings.probable.operationCostEscalationPercent),
        default_useful_life_years: Number(this.scenarioSettings.probable.defaultUsefulLifeYears),
        inflow_factor_percent: Number(this.scenarioSettings.probable.inflowFactorPercent),
        outflow_factor_percent: Number(this.scenarioSettings.probable.outflowFactorPercent),
        degradation_factor_percent: Number(this.scenarioSettings.probable.degradationFactorPercent),
        // Leere Felder als `null` senden und nicht als 0: der Serializer liest
        // `null` als „keine Herleitung", 0 dagegen als Zinssatz null Prozent.
        equity_share_percent: toZahlOderNull(this.equitySharePercent),
        equity_rate_percent: toZahlOderNull(this.scenarioSettings.probable.equityRatePercent),
        debt_rate_percent: toZahlOderNull(this.scenarioSettings.probable.debtRatePercent),
        scenario_settings: this.scenarioSettings,
      };
    },
    calcActionsCalcList() {
      /*
       * `forCalc` ist die Auswahl, die das Häkchen „Maßnahme berechnen" auf
       * der Maßnahmenseite setzt; sie kommt vom Server (`Action.checked`).
       *
       * Vorher wurde hier gegen `noCalc` gefiltert — eine ältere, rein lokale
       * Liste, die niemand mehr füllt. Der Filter lief also immer ins Leere und
       * diese Ansicht rechnete **alle** Maßnahmen, auch die abgewählten. Das
       * Einsparpotential war die einzige Ansicht, die es richtig gemacht hat,
       * weil es `forCalc` verwendet.
       */
      const checked = new Set(this.store.forCalc);
      this.actionCalcList = this.actionList
        .map((item) => item.id)
        .filter((id) => checked.has(id));
    },
    calcConsumerMitGenList() {
      const actionsForCalc = this.actionList.filter((item) => this.actionCalcList.includes(item.id));
      const result = {};

      actionsForCalc.forEach((item) => {
        if (Array.isArray(item.generator_type) && item.generator_type.length > 0) {
          result[item.id] = (result[item.id] || []).concat(item.generator_type);
        }
      });
      return result;
    },
    buildUsefulLifePayload() {
      return Object.entries(this.usefulLifeYears).reduce((acc, [key, value]) => {
        if (value) {
          acc[key] = Number(value);
        }
        return acc;
      }, {});
    },
    buildScenarioRequestSettings(key) {
      const scenario = this.scenarioSettings[key];
      const settings = {
        analysisYears: Number(scenario.analysisYears),
        discountRatePercent: Number(scenario.discountRatePercent),
        energyPriceEscalationPercent: Number(scenario.energyPriceEscalationPercent),
        operationCostEscalationPercent: Number(scenario.operationCostEscalationPercent),
        defaultUsefulLifeYears: Number(scenario.defaultUsefulLifeYears),
        inflowFactorPercent: Number(scenario.inflowFactorPercent),
        outflowFactorPercent: Number(scenario.outflowFactorPercent),
        degradationFactorPercent: Number(scenario.degradationFactorPercent),
      };

      /*
       * Die WACC-Eingaben müssen mit, sonst leitet der Server nichts her.
       *
       * `normalize_settings` in `projects/valeri.py` entscheidet so:
       *
       *     zins_vorgegeben  = 'discountRatePercent' kam mit
       *     wacc_vorgegeben  = eine der drei WACC-Grössen kam mit
       *     aus_wacc = hergeleitet is not None and (wacc_vorgegeben or not zins_vorgegeben)
       *
       * Diese Ansicht schickt immer einen `discountRatePercent` — jedes
       * Szenario hat seinen eigenen, das ist der Sinn der Szenarien. Ohne die
       * drei Grössen daneben ist `wacc_vorgegeben` falsch und `aus_wacc`
       * damit **immer** falsch: der Hinweis über der Tabelle versprach eine
       * Herleitung, gerechnet wurde weiter mit dem gesetzten Zinssatz.
       *
       * Der Bericht geht denselben Weg ohne `settings_data`, dort ist
       * `zins_vorgegeben` falsch und die Herleitung griff. Seite und Bericht
       * wiesen deshalb verschiedene Kapitalwerte aus, sobald die drei Felder
       * gefüllt waren.
       *
       * Nur mitschicken, was gesetzt ist: der Eigenkapitalanteil gilt
       * projektweit, die beiden Zinssätze stehen je Szenario in der Tabelle.
       * Fehlt eine der drei Angaben, ist `hergeleitet` ohnehin `None` und der
       * gesetzte Zinssatz bleibt stehen.
       */
      const anteil = toZahlOderNull(this.equitySharePercent);
      const eigen = toZahlOderNull(scenario.equityRatePercent);
      const fremd = toZahlOderNull(scenario.debtRatePercent);
      if (anteil !== null) settings.equitySharePercent = anteil;
      if (eigen !== null) settings.equityRatePercent = eigen;
      if (fremd !== null) settings.debtRatePercent = fremd;

      return settings;
    },
    applyReturnedUsefulLives() {
      const probableActions = [
        ...(this.scenarioResponses.probable?.result?.actions || []),
        ...((this.scenarioResponses.probable?.result?.actionGroups || []).flatMap((group) => group.actions || [])),
      ];
      probableActions.forEach((action) => {
        this.usefulLifeYears[action.id] = action.usefulLifeYears;
      });
    },
    calcValeri() {
      this.loading = true;
      this.errorResponse = null;
      this.calcActionsCalcList();
      const consMitGens = this.calcConsumerMitGenList();
      const usefulLifeYears = this.buildUsefulLifePayload();

      const requests = ["probable", "worstCase", "bestCase"].map((scenarioKey) =>
        api.post(`/projects/${this.projectId}/calc/valeri/`, {
          params: {
            actions: this.actionCalcList,
            ConsMitGen_ids: consMitGens,
          },
          settings: this.buildScenarioRequestSettings(scenarioKey),
          usefulLifeYears,
        })
      );

      return Promise.all(requests)
        .then(([probableResponse, worstCaseResponse, bestCaseResponse]) => {
          this.scenarioResponses = {
            probable: probableResponse.data,
            worstCase: worstCaseResponse.data,
            bestCase: bestCaseResponse.data,
          };
          // Eingeklappt starten, solange der Schalter „Gruppendetails" aus ist
          // — sonst widerspräche die Tabelle gleich nach dem Rechnen wieder
          // dem Schalter.
          const eingeklappt = (response) => (this.showGroupDetails
            ? []
            : (response.data?.result?.actionGroups || []).map((group) => group.id));
          this.collapsedGroupsByScenario = {
            probable: eingeklappt(probableResponse),
            worstCase: eingeklappt(worstCaseResponse),
            bestCase: eingeklappt(bestCaseResponse),
          };
          // Abbild der Eingaben festhalten, mit denen gerade gerechnet wurde —
          // daran erkennt `settingsDirty` spätere Änderungen.
          this.calculatedSettings = JSON.parse(JSON.stringify(this.scenarioSettings));
          this.calculatedEquitySharePercent = toZahlOderNull(this.equitySharePercent);
          this.applyReturnedUsefulLives();
          if (this.hasAnyVisualizationData) {
            this.requestChartRedraw();
          }
        })
        .catch((error) => {
          const detail = error.response?.data;
          this.errorResponse = Array.isArray(detail)
            ? detail.join(" ")
            : detail?.detail || "Die VALERI-Berechnung konnte nicht durchgeführt werden.";
        })
        .finally(() => {
          this.loading = false;
        });
    },
    saveDefaults() {
      this.loading = true;
      api
        .patch(`/projects/${this.projectId}/valeri_settings/`, this.toApiSettingsPayload())
        .then(() => {
          toast.success("VALERI-Parameter gespeichert.");
          this.merkeStand();
          return this.calcValeri();
        })
        .catch(() => {
          toast.error("VALERI-Parameter konnten nicht gespeichert werden.");
        })
        .finally(() => {
          this.loading = false;
        });
    },
    getMethodName(type) {
      return this.methodTexts?.[type] || type || "-";
    },
    /*
     * Vorher `Number(value) || 0` — damit wurden `null`, `undefined` und `NaN`
     * alle zu einer sauberen „0". Die Maßnahmentabelle benutzt diese Funktion
     * in jeder Zahlenspalte, also stand in der Zeile einer Maßnahme, deren
     * Berechnung durchgefallen war, eine vollständige Null-Bilanz — von einer
     * echten Null nicht zu unterscheiden.
     *
     * Das wiegt hier besonders schwer, weil `actions/models.py:calc()` Fehler
     * je Seite abfängt und weiterläuft (siehe copilot.md): ein still falsches
     * Ergebnis sieht ohnehin schon nach Erfolg aus.
     */
    formatNumber(value) {
      if (value === null || value === undefined || value === "") {
        return "-";
      }
      const zahl = Number(value);
      return Number.isFinite(zahl)
        ? new Intl.NumberFormat("de-DE", { maximumFractionDigits: 0 }).format(zahl)
        : "-";
    },
    formatPayback(value) {
      if (value === null || value === undefined) {
        return "-";
      }
      const zahl = Number(value);
      return Number.isFinite(zahl) ? zahl.toFixed(1) : "-";
    },
    formatEuro(value) {
      if (value === null || value === undefined) {
        return "-";
      }
      return `${this.formatNumber(value)} €`;
    },
    buildScenarioSeries(key, labels) {
      const points = this.scenarioResponses[key]?.result?.total?.cashflows || [];
      const yearMap = points.reduce((acc, item) => {
        const year = Number(item.year);
        acc[year] = Number(item.cumulativeDiscountedCashflow);
        return acc;
      }, {});

      return labels.map((label) => {
        const value = yearMap[Number(label)];
        return Number.isFinite(value) ? value : null;
      });
    },
    getDiscountedResidualContribution(scenarioKey) {
      // Über `calculatedScenario`, damit Laufzeit und Zinssatz dieselben sind,
      // mit denen der Restwert entstanden ist — einschliesslich eines aus dem
      // WACC hergeleiteten Zinssatzes, den `scenarioSettings` nicht kennt.
      const gerechnet = this.calculatedScenario(scenarioKey);
      const residualValue = Number(this.scenarioResponses[scenarioKey]?.result?.total?.residualValue);
      const analysisYears = Number(gerechnet.analysisYears);
      const discountRatePercent = Number(gerechnet.discountRatePercent);

      if (!Number.isFinite(residualValue) || residualValue <= 0) {
        return 0;
      }
      if (!Number.isFinite(analysisYears) || analysisYears < 0) {
        return 0;
      }

      const discountRate = Number.isFinite(discountRatePercent) ? discountRatePercent / 100 : 0;
      const denominator = (1 + discountRate) ** analysisYears;
      if (!Number.isFinite(denominator) || denominator <= 0) {
        return 0;
      }

      return residualValue / denominator;
    },
    /*
     * Bringt die Diagramme auf ihre aktuelle Größe.
     *
     * Vorher erhöhte diese Methode `chartRenderKey`, und weil dieser Schlüssel
     * im `:key` **aller sieben** Diagramme stand, wurden sie 250 ms nach jedem
     * Rechnen abgerissen und neu aufgebaut. Das war nicht nur das Flackern der
     * Seite, es warf auch:
     *
     *     TypeError: Cannot read properties of null (reading 'ownerDocument')
     *       at getComputedStyle   (chart.js)
     *       at getMaximumSize
     *       at Chart._resize
     *       at detached
     *       at Chart.bindResponsiveEvents
     *       at Chart.bindEvents
     *       at Chart._checkEventBindings
     *       at Chart.update
     *
     * Im Browser nachgestellt: beim Schlüsselwechsel nimmt Vue den alten
     * Canvas aus dem Dokument, während vue-chartjs auf derselben Instanz noch
     * ein `update()` abarbeitet. Chart.js merkt dabei „detached", will die
     * Größe neu messen und fragt `getComputedStyle(canvas.parentNode)` — und
     * `parentNode` ist bereits `null`.
     *
     * Der Neuaufbau ist seit Chart.js 3 ohnehin gegenstandslos: jeder Canvas
     * hängt an einem eigenen `ResizeObserver`, und vue-chartjs schiebt
     * Änderungen an `data` und `options` von selbst nach. Die `:key`-Angaben
     * sind deshalb entfallen; hier bleibt nur noch ein `resize()` für den Fall,
     * dass sich das Layout um ein Diagramm herum geändert hat.
     *
     * `parentNode`-Prüfung, weil genau deren Fehlen oben den Absturz ergab.
     */
    requestChartRedraw() {
      if (this._redrawTimer) {
        window.clearTimeout(this._redrawTimer);
      }
      this._redrawTimer = window.setTimeout(() => {
        this._redrawTimer = null;
        for (const refName of VALERI_CHART_REFS) {
          const chart = this.getChartByRef(refName, true);
          if (chart?.canvas?.parentNode) {
            chart.resize();
          }
        }
      }, 250);
    },
    toExportNumber(value) {
      if (value === null || value === undefined) {
        return "";
      }
      const normalized = Number(value);
      return Number.isFinite(normalized) ? normalized : "";
    },
    /*
     * `quiet`, weil nicht jedes der sieben Diagramme immer sichtbar ist: die
     * `has…Data`-Bedingungen blenden Karten aus, und dann gibt es die `ref`
     * schlicht nicht. Beim Durchlauf über alle Diagramme (`requestChartRedraw`)
     * wäre die Warnung reines Rauschen; beim Herunterladen eines einzelnen
     * Diagramms ist sie dagegen der Hinweis, den man sehen will.
     */
    getChartByRef(refName, quiet = false) {
      const chartComponent = this.$refs[refName];
      if (!chartComponent) {
        if (!quiet) {
          console.warn(`Chart ref '${refName}' not found.`);
        }
        return null;
      }
      if (Array.isArray(chartComponent)) {
        const instance = chartComponent[0];
        return instance?.chart || (instance?.$data && instance.$data._chart) || instance?._chart || null;
      }
      return chartComponent?.chart || (chartComponent?.$data && chartComponent.$data._chart) || chartComponent?._chart || null;
    },
    downloadChartImageByRef(refName, filePrefix) {
      const chart = this.getChartByRef(refName);
      if (!chart) {
        toast.warning("Grafik ist noch nicht bereit. Bitte Panel öffnen und erneut versuchen.");
        return;
      }
      try {
        const link = document.createElement("a");
        link.download = `${filePrefix}-projekt-${this.projectId}.png`;

        if (chart.canvas && typeof chart.canvas.toBlob === "function") {
          chart.canvas.toBlob((blob) => {
            if (!blob) {
              toast.error("Grafik konnte nicht erstellt werden.");
              return;
            }
            const url = URL.createObjectURL(blob);
            link.href = url;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
          }, "image/png");
          return;
        }

        const dataUrl = chart.toBase64Image();
        link.href = dataUrl;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error) {
        console.error("Chart image download failed", error);
        toast.error("Grafik konnte nicht heruntergeladen werden.");
      }
    },
    exportChartDataExcelGeneric(chartData, filePrefix) {
      return exportChartExcel(this.projectId, chartData, filePrefix);
    },
    chartToBase64(chart) {
      return new Promise((resolve, reject) => {
        if (!chart) {
          resolve(null);
          return;
        }

        if (chart.canvas && typeof chart.canvas.toBlob === "function") {
          chart.canvas.toBlob((blob) => {
            if (!blob) {
              resolve(null);
              return;
            }
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          }, "image/png");
          return;
        }

        if (typeof chart.toBase64Image === "function") {
          resolve(chart.toBase64Image());
          return;
        }

        resolve(null);
      });
    },
    async collectValeriChartsForDocx() {
      const descriptors = [
        { id: "valeriScenarioChart", title: "Szenarien der Kapitalwertentwicklung", module: "VALERI", enabled: this.hasChartData },
        { id: "valeriTornadoChart", title: "NPV Tornado je Maßnahme", module: "VALERI", enabled: this.hasNpvTornadoData },
        { id: "valeriCashflowChart", title: "Jährlicher Cashflow je Maßnahme", module: "VALERI", enabled: this.hasActiveCashflowStackData },
        { id: "valeriPaybackChart", title: "Dynamische Amortisation je Maßnahme", module: "VALERI", enabled: this.hasPaybackTimelineData },
        { id: "valeriAnnuityChart", title: "Annuitätsvergleich je Maßnahme", module: "VALERI", enabled: this.hasAnnuityComparisonData },
        { id: "valeriRadarChart", title: "Sensitivität der Szenarioparameter", module: "VALERI", enabled: this.hasSensitivityRadarData },
        { id: "valeriIrrChart", title: "Interner Zinsfuß (IRR) je Maßnahme", module: "VALERI", enabled: this.hasIrrComparisonData },
      ];

      const active = descriptors.filter((entry) => entry.enabled);
      const charts = [];
      for (const entry of active) {
        const chart = this.getChartByRef(entry.id);
        const imageBase64 = await this.chartToBase64(chart);
        charts.push({
          id: entry.id,
          title: entry.title,
          module: entry.module,
          imageBase64,
        });
      }
      return charts;
    },
    async exportDocxWithAppendix() {
      this.isDocxExporting = true;
      try {
        this.requestChartRedraw();
        await new Promise((resolve) => window.setTimeout(resolve, 350));

        const charts = await this.collectValeriChartsForDocx();
        storeDocxModuleCharts(this.projectId, "VALERI", charts);
        const response = await api.post(
          `/projects/${this.projectId}/docx_extended/`,
          {
            charts,
            meta: {
              source: "valeri",
              schemaVersion: "1.0",
              exportedAt: new Date().toISOString(),
            },
          },
          { responseType: "blob" },
        );

        const url = URL.createObjectURL(response.data);
        const link = document.createElement("a");
        link.href = url;
        const disposition = response.headers?.["content-disposition"] || "";
        const match = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        link.download = match?.[1]?.replace(/['"]/g, "") || `Einsparpotentialbericht_inkl_Anhang_${this.projectId}.docx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } catch {
        toast.error("DOCX Export konnte nicht erstellt werden.");
      } finally {
        this.isDocxExporting = false;
      }
    },
    async exportDin16247Docx() {
      this.isDinExporting = true;
      try {
        this.requestChartRedraw();
        await new Promise((resolve) => window.setTimeout(resolve, 350));

        // Refresh VALERI charts, then combine with cached Energiebilanz/Benchmarking charts.
        const valeriCharts = await this.collectValeriChartsForDocx();
        storeDocxModuleCharts(this.projectId, "VALERI", valeriCharts);
        const charts = getDocxCombinedCharts(this.projectId);

        const response = await api.post(
          `/projects/${this.projectId}/din16247_docx/`,
          {
            charts,
            meta: {
              source: "din16247",
              schemaVersion: "1.0",
              exportedAt: new Date().toISOString(),
            },
          },
          { responseType: "blob" },
        );

        const url = URL.createObjectURL(response.data);
        const link = document.createElement("a");
        link.href = url;
        const disposition = response.headers?.["content-disposition"] || "";
        const match = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        link.download = match?.[1]?.replace(/['"]/g, "") || `Energieaudit_DIN_EN_16247-1_${this.projectId}.docx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } catch {
        toast.error("DIN 16247 Bericht konnte nicht erstellt werden.");
      } finally {
        this.isDinExporting = false;
      }
    },
    async exportValeriExcel() {
      this.isExporting = true;
      try {
        const scenarios = {
          probable: this.scenarioResponses.probable?.result || {},
          worstCase: this.scenarioResponses.worstCase?.result || {},
          bestCase: this.scenarioResponses.bestCase?.result || {},
        };

        const response = await api.post(
          `/projects/${this.projectId}/valeri_excel/`,
          {
            scenarioSettings: this.scenarioSettings,
            scenarios,
          },
          { responseType: "blob" },
        );

        const url = URL.createObjectURL(response.data);
        const link = document.createElement("a");
        link.href = url;
        const disposition = response.headers?.["content-disposition"] || "";
        const match = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        link.download = match?.[1]?.replace(/['"]/g, "") || `VALERI-Projekt-${this.projectId}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } catch {
        toast.error("VALERI konnte nicht exportiert werden.");
      } finally {
        this.isExporting = false;
      }
    },
  },
};
</script>

<style scoped>
/* Wie in ProjectEnergyFlow.vue: feste helle Flaeche unter themengefaerbter
 * Schrift, im dunklen Modus unlesbar. */
.group-row {
  background-color: rgb(var(--v-theme-surface-variant));
  font-weight: bold;
}

.total-row {
  font-weight: bold;
}

.useful-life-cell {
  min-width: 120px;
}

.useful-life-input {
  max-width: 90px;
  margin-left: auto;
}

.scenario-input-cell {
  min-width: 170px;
}

.chart-wrapper {
  position: relative;
  min-height: 420px;
}

.chart-section {
  width: 100%;
}

.radar-wrapper {
  min-height: 460px;
}

/*
 * Kopfzeile einer Diagrammkarte: Titel links, Hinweis- und Optionsschaltflaeche
 * rechts beieinander.
 *
 * Vorher hing die Optionsschaltflaeche mit `position: absolute; top: 8px;
 * right: 8px` an der Karte, waehrend die Hinweisschaltflaeche gleich hinter
 * dem Titeltext stand. Auf den ganzseitigen Karten der Energiebilanz faellt
 * das nicht auf; hier sind die Karten aber nur halb so breit (`:md="6"`) und
 * die Titel lang („Jaehrlicher Cashflow je Massnahme (aktives Szenario)").
 * Der Titel lief deshalb unter die Optionsschaltflaeche, und die beiden
 * Schaltflaechen standen weit auseinander.
 *
 * Jetzt steht das Menue im Fluss, `<v-spacer />` schiebt beide nach rechts.
 */
.chart-title {
  font-weight: 600;
  margin-bottom: 8px;
  column-gap: 4px;
}

/* Ohne `min-width: 0` darf ein Flex-Element nicht unter seine Inhaltsbreite
   schrumpfen — der lange Titel sprengte damit die Karte, statt umzubrechen. */
.chart-title-text {
  min-width: 0;
  line-height: 1.3;
}

div.heading {
  color: rgb(var(--v-theme-on-surface));
  font-size: 1.25rem;
}
</style>


